import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import './Content.css';
import CustomSwitch from './CustomSwitch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BackspaceIcon from '@mui/icons-material/Backspace';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { apiClient, isApiError } from '../../../lib/apiClient';
import { shareInFlightRequest } from '../../../lib/sharedApiRequest';
import { clearAllCreatorOptionsCache } from '../../../lib/creatorOptionsIndexedDb';
import { Report } from '../SearchArea/WeeklyMasterProvider';
import { useUserName } from '../../../context/UserNameContext';
import { ReadyState } from 'react-use-websocket';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAppWebSocket } from '../../../context/WebSocketContext';
import { useUser } from '../../../context/UserContext';
import { useUserProfile } from '../../../context/UserProfileContext';
import { sendStatusUsing, sendStatusRelease } from '../../WebSocket/statusMessage';
import ConfirmDialog from '../../common/ConfirmDialog';
import { Snackbar as AppSnackbar } from '../../common/Snackbar/Snackbar';
import { postWeeklyReportRemand, postNewWeeklyReportVersion } from '../../../api/weeklyReport';
import VersionHistorySidebar from '../../common/Sidebar/VersionHistorySidebar';
import ImageAddDialog, {
  type ImageAddCandidate,
} from '../../common/ImageAddDialog/ImageAddDialog';
import ImageAttachmentsGrid from '../../common/ImageAddDialog/ImageAttachmentsGrid';
import type { ImageAttachment } from '../../../types/imageAttachment';
import { postPresignedDownloadUrlsWithMeta } from '../../../api/imageDownload';
import { uploadImageAttachments } from '../../common/ImageAddDialog/ImageUploadManager';
import { useTextareaAutoResize } from './hooks/useTextareaAutoResize';
import { buildRowIdentity } from './helpers/rowIdentity';
import type { RowLockOwner } from './helpers/lockState';
import { filterReportTypesForOrg, resolveReportTypeAttrId } from './helpers/reportTypes';
import {
  normalizeReportTypeSelectionKey,
  resolveKnownMultiSelectTokens,
} from '../../common/utils/reportTypeTokens';
import {
  deleteEnlightSyncOverride,
  readEnlightSyncOverrides,
  resolveWeeklyReportEnlightSyncStates,
  writeEnlightSyncOverride,
  writeRemandEnlightSyncPreference,
} from '../../InputEdit/enlightSync';
import {
  WeeklyReportResultRow,
  type WeeklyReportResultRowContext,
} from './components/WeeklyReportResultRow';
import { WeeklyReportRowCard } from './components/WeeklyReportRowCard';
import {
  StatusIndicator,
  SelfEditingBadge,
  RowSavingBadge,
  FieldEndActions,
  LinkifyDisplay,
  getCaretIndexFromPoint,
  useIsBrowserTranslated,
  TranslatableDisplayField,
  TranslatableDisplayTextarea,
} from './components/ContentUiParts';
import {
  buildWeeklyReportPublicScopeDisplayMap,
  DEFAULT_PUBLIC_SCOPE_DISPLAY,
  type PublicScopeDisplay,
  type WeeklyReportPublicSettingRow,
} from '../../../lib/weeklyReportPublicScopeDisplay';
import {
  canCompleteFeedbackSave,
  isRecentSelfRelease,
  resolveFeedbackAfterServerRefresh,
} from './helpers/feedbackPersistence';

// -----------------------------------------------------------------------------
// Content (main orchestrator)
// - Handles weekly report state, server sync, websocket presence, and rendering.
// - Row-level rendering details are delegated to separated components/helpers.
// -----------------------------------------------------------------------------

// API / local persistence settings used by this screen.
const API_POST_PATH = '/weekly-report-post';
const API_REQUIRED_ITEMS = '/weekly-required-items';
const API_GET_WR_PUBLIC_SETTING = '/get_wr_public_setting';
const API_COMMON_ATTRIBUTE_META_SETTINGS = '/get_common_attribute_meta_settings';
const API_GET_COMMUNICATION_NOTICE = '/get_communication_notice';
const LOADING_PUBLIC_SCOPE_DISPLAY: PublicScopeDisplay = {
  summaryText: '確認中',
  tooltipText: '公開範囲を読み込んでいます',
};
const AUTO_VERSION_SAVE_EVERY_COMBINED = 5;
const DRAFT_DB_NAME = 'weekly-report-offline';
const DRAFT_STORE_NAME = 'wr-drafts';
const DRAFT_SAVE_DEBOUNCE_MS = 400;
const DRAFT_QUEUE_RETRY_MS = 15000;

const WEEKLY_REPORT_UPDATED_EVENT = 'weekly-report-updated';
const WEEKLY_REPORT_UPDATED_AT_KEY = 'weekly-report-updated-at';

// Notify SearchArea and sibling tabs that data has changed.
type WeeklyReportUpdateSource = 'general' | 'report-type';

function notifyWeeklyReportUpdated(source: WeeklyReportUpdateSource = 'general') {
  const nowIso = new Date().toISOString();

  try {
    localStorage.setItem(WEEKLY_REPORT_UPDATED_AT_KEY, nowIso);
  } catch {
  }

  // 週報の削除などで発行者が増減し得るため、候補キャッシュを破棄する。
  if (source === 'general') void clearAllCreatorOptionsCache();

  try {
    window.dispatchEvent(
      new CustomEvent(WEEKLY_REPORT_UPDATED_EVENT, {
        detail: { source, updatedAt: nowIso },
      })
    );
  } catch {
    window.dispatchEvent(new Event(WEEKLY_REPORT_UPDATED_EVENT));
  }
}

const FIELD_ACTION_PADDING_RIGHT_PX = 120;
const BODY_MIN_ROWS = 5;
const BODY_LINE_HEIGHT = 1.25;
const INITIAL_VISIBLE_COUNT = 30;
const LOAD_MORE_COUNT = 30;

// Screen-local domain types.
type ReportTypeOption = {
  id: number;                // option_id
  label: string;             // 表示ラベル（例: 内報）
  value: string;             // 値（例: 内報）
  color?: string | null;     // HEX など（任意）
  attr_id: number;           // このオプションが属する属性ID（＝週報分類の attr_id）
  department_id?: number | null;     // 部門
  team_id?: number | null;           // チーム
  order_number?: number | null;
};

type WeeklyRequiredItemsResponse = {
  reportTypes: ReportTypeOption[];
  departments: Array<any>;
  teams: Array<any>;
  creators: Array<any>;
};

type CommonAttributeMetaSetting = {
  attr_id: number;
  department_id?: number | null;
  team_id?: number | null;
  multiple_select?: boolean | number | string;
};

type SaveStatus = 'idle' | 'editing' | 'saving';
type FixedSortKey = 'updated' | 'range' | 'creator' | 'deptTeam';
type AttrSortKey = `attr_${number}`;
type SortKey = FixedSortKey | AttrSortKey;
type SortDirection = 'asc' | 'desc';
type SortSelection = { key: SortKey; dir: SortDirection };

type SortableColumn = {
  attr_id: number;
  attr_name: string;
  options: Array<{ option_id: number; order_number: number }>;
};

type DraftRecord = {
  wr_id: number;
  title: string;
  body: string;
  feedback_body: string;
  sync_flg: number;
  update_by?: string;
  update_by_user_id?: string;
  updatedAt: number;
  dirty: boolean;
  titleDirty: boolean;
  bodyDirty: boolean;
  feedbackDirty: boolean;
};

type CommunicationNotice = {
  wr_id?: number;
  wrId?: number;
  id?: number;
  chat_count?: number;
  chatCount?: number;
  reply_count?: number;
  replyCount?: number;
  chats?: Array<{ notice_users?: string[] | null } | null> | null;
  replies?: Array<{ notice_users?: string[] | null } | null> | null;
};

type ReportFileRecord = {
  file_id?: number;
  fileId?: number;
  object_key?: string;
  objectKey?: string;
  object_key_hash?: string;
  objectKeyHash?: string;
  original_name?: string;
  originalName?: string;
  signed_url?: string;
  signedUrl?: string;
  url?: string;
  public_url?: string;
  publicUrl?: string;
  status?: string;
  [key: string]: any;
};

const LOCKED_BG = '#fff';
const LOCKED_FG = 'text.primary';
const SAVING_BG = '#21e77a';
const SAVING_SHADOW = '0 0 0 2px rgba(60, 179, 113, 0.35)';
const RIGHT_COL_SX = {
  display: 'flex',
  flexDirection: 'column' as const,
  minWidth: 0,
  gap: 1,
};

const TEXTAREA_WRAP_SX = {
  flex: 1,
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  px: 1,
  pb: 0,
  minWidth: 0,
};
const SORT_SELECT_BASE_SX = {
  minWidth: 140,
  '& .MuiInputBase-input': { fontSize: '12px' },
  '& .MuiInputLabel-root': { fontSize: '12px' },
};
const BTN_ROW_SX = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: 1,
  px: 1,
  pt: 0,
  pb: 0,
  mt: '10px',
  mb: '4px',
  '& > button': { flex: '1 1 calc(33.333% - 8px)' },
};
const FEEDBACK_LABEL_SX = {
  pb: 0.5,
  mt: 1,
  px: 1,
  display: 'flex',
  alignItems: 'center',
};
const ARTICLES_HEADER_SX = {
  backgroundColor: '#2f2f2f',
  color: '#fff',
  px: 2,
  py: 0,
  borderRadius: '4px 4px 0 0',
  m: 0,
};

const HEADER_ACTION_GROUP_SX = {
  display: 'flex',
  gap: 1,
};

const HEADER_ACTION_BUTTON_SX = {
  minWidth: 0,
  p: 0,
  mx: 0.5,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
};

const HEADER_ICON_STROKE_SX = {
  color: 'rgb(211, 211, 211)',
  shapeRendering: 'geometricPrecision',
  filter: 'drop-shadow(0 0 1px rgb(39, 65, 211))',
  '& path': {
    stroke: 'rgb(39, 65, 211)',
    strokeWidth: 1,
    vectorEffect: 'non-scaling-stroke',
    paintOrder: 'stroke fill',
    strokeLinejoin: 'round',
    strokeLinecap: 'round',
  },
};

const HEADER_CAPTION_SX = {
  lineHeight: 1,
  fontSize: '10px',
  color: '#fff',
  textShadow:
    '1px 0 0 #736fc2ff, -1px 0 0 #736fc2ff, 0 1px 0 #736fc2ff, 0 -1px 0 #736fc2ff',
};

const HEADER_ICON_STROKE_RED_SX = {
  '& path': {
    stroke: '#fff',
    strokeWidth: 2,
    paintOrder: 'stroke fill',
    strokeLinejoin: 'round',
    strokeLinecap: 'round',
  },
};

const HEADER_CAPTION_RED_SX = {
  lineHeight: 1,
  fontSize: '10px',
  color: 'error.main',
  textShadow:
    '1px 0 0 #fff, -1px 0 0 #fff, 0 1px 0 #fff, 0 -1px 0 #fff, 1px 1px 0 #fff, -1px -1px 0 #fff, -1px 1px 0 #fff, 1px -1px 0 #fff',
};

const TITLE_ACTIVE_COLOR = 'rgb(39, 65, 211)';
const BODY_TEXT_COLOR = 'rgb(24, 5, 0)';
const TITLE_ACTIVE_TEXT_SX = {
  color: TITLE_ACTIVE_COLOR,
  textDecorationLine: 'underline',
  textDecorationColor: 'currentColor',
  textUnderlineOffset: '2px',
  textDecorationThickness: '1px',
} as const;

interface ContentProps {
  results: Report[];
  searchPerformed: boolean;
  onEdit?: (edited: Report[]) => void;
  onMailExportReportsChange?: (reports: Report[]) => void;
}

// Treat edge-only whitespace/newline changes as "no diff".
const equalIgnoringEdgeWhitespace = (a: string, b: string): boolean => {
  if (a === b) return true;
  return a.trim() === b.trim();
};

const areSameReportListByReference = (a: Report[], b: Report[]): boolean =>
  a.length === b.length && a.every((item, idx) => item === b[idx]);

const Content: React.FC<ContentProps> = ({
  results,
  onEdit,
  searchPerformed,
  onMailExportReportsChange,
}) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';

  // Current user context and browser translation mode.
  const userName = useUserName();
  const user = useUser();
  const { profile } = useUserProfile();
  const isBrowserTranslated = useIsBrowserTranslated();

  const selfLoginId = useMemo(() => {
    const raw =
      (user as any)?.signInDetails?.loginId ||
      (user as any)?.attributes?.email ||
      (user as any)?.username;
    return raw ? String(raw).trim() : '';
  }, [user]);

  const UPDATE_BY = userName;
  const UPDATE_BY_USER_ID = selfLoginId;

  const [sortableColumns, setSortableColumns] = useState<SortableColumn[]>([]);

  useEffect(() => {
    if (!Array.isArray(results) || results.length === 0) {
      setSortableColumns([]);
      return;
    }
    const dept = Number(results[0]?.department ?? 0);
    const team = Number(results[0]?.team ?? 0);

    if (dept === 0) {
      setSortableColumns([]);
      return;
    }

    let cancelled = false;
    // team_id を指定して呼ぶことでバックエンドのdedup（チーム別COMMON優先）が適用され
    // チーム固有の use_for_sort が返る
    apiClient.get('/get_attributes', { params: { department_id: dept, team_id: team } })
      .then((resp) => {
        if (cancelled) return;
        const attrs: any[] = Array.isArray(resp?.data) ? resp.data : [];
        const cols: SortableColumn[] = attrs
          .filter((a) => {
            if (!a.use_for_sort || !Array.isArray(a.options) || a.options.length === 0) return false;
            const aDept = Number(a.department_id ?? 0);
            const aTeam = Number((a as any).team_id ?? 0);
            return results.every((r) => {
              const rDept = Number(r.department ?? 0);
              const rTeam = Number(r.team ?? 0);
              // aDept===0 はグローバル属性（COMMONレコードなしの場合のフォールバック）
              return (aDept === 0 || aDept === rDept) && (aTeam === 0 || aTeam === rTeam);
            });
          })
          .map((a) => ({
            attr_id: a.attr_id,
            attr_name: a.label ?? String(a.attr_id),
            options: (a.options ?? []).map((o: any) => ({
              option_id: Number(o.option_id ?? o.id ?? 0),
              order_number: Number(o.order_number ?? 0),
            })),
          }));
        setSortableColumns(cols);
      }).catch(() => {});
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, searchPerformed]);

  // Row-scoped UI states (images, loading, dialog targets).
  const [imageAttachmentsByKey, setImageAttachmentsByKey] = useState<Record<string, ImageAttachment[]>>({});
  const [fileAttachmentsByKey, setFileAttachmentsByKey] = useState<Record<string, ImageAttachment[]>>({});
  const fileAttachmentsRequestIdRef = useRef(0);
  const [imageUploadByKey, setImageUploadByKey] = useState<Record<string, boolean>>({});
  const [imageDialogKey, setImageDialogKey] = useState<{ key: string; wrId: number | null } | null>(null);
  const [imageDeleteTarget, setImageDeleteTarget] = useState<{
    key: string;
    id: string;
    wrId?: number | null;
  } | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const loadMoreLockRef = useRef(false);

  const titleInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const pendingTitleCaretRef = useRef<Record<number, number>>({});
  const bodyInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const pendingBodyCaretRef = useRef<Record<number, number>>({});

  const feedbackInputRefs = useRef<Record<number, HTMLTextAreaElement | null>>({});
  const pendingFeedbackCaretRef = useRef<Record<number, number>>({});

  const setTitleInputRef = useCallback(
    (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => {
      titleInputRefs.current[rowKeyNum] = el;
      if (!el) return;

      const pending = pendingTitleCaretRef.current[rowKeyNum];
      if (typeof pending !== 'number') return;

      const apply = () => {
        const cur = titleInputRefs.current[rowKeyNum];
        if (!cur) return;

        const len = (cur.value ?? '').length;
        const pos = Math.max(0, Math.min(pending, len));

        cur.focus();
        try {
          cur.setSelectionRange(pos, pos);
        } catch {
        }
        delete pendingTitleCaretRef.current[rowKeyNum];
      };

      requestAnimationFrame(apply);
    },
    []
  );

  const {
    autoResizeTextarea,
    scheduleScrollCaretIntoView,
    getTextareaFromEvent,
    handleResizeStart,
    handleResizeEnd,
  } = useTextareaAutoResize({
    minRows: BODY_MIN_ROWS,
    lineHeightRatio: BODY_LINE_HEIGHT,
  });

  const setBodyInputRef = useCallback(
    (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => {
      bodyInputRefs.current[rowKeyNum] = el;
      if (!el) return;

      const pending = pendingBodyCaretRef.current[rowKeyNum];
      if (typeof pending !== 'number') return;

      const apply = () => {
        const cur = bodyInputRefs.current[rowKeyNum];
        if (!cur) return;

        const len = (cur.value ?? '').length;
        const pos = Math.max(0, Math.min(pending, len));

        cur.focus();
        try {
          cur.setSelectionRange(pos, pos);
        } catch {
        }

        autoResizeTextarea(cur);
        scheduleScrollCaretIntoView(cur);

        delete pendingBodyCaretRef.current[rowKeyNum];
      };

      requestAnimationFrame(apply);
    },
    [autoResizeTextarea, scheduleScrollCaretIntoView]
  );

  const setFeedbackInputRef = useCallback(
    (rowKeyNum: number) => (el: HTMLTextAreaElement | null) => {
      feedbackInputRefs.current[rowKeyNum] = el;
      if (!el) return;

      const pending = pendingFeedbackCaretRef.current[rowKeyNum];
      if (typeof pending !== 'number') return;

      const apply = () => {
        const cur = feedbackInputRefs.current[rowKeyNum];
        if (!cur) return;

        const len = (cur.value ?? '').length;
        const pos = Math.max(0, Math.min(pending, len));

        cur.focus();
        try {
          cur.setSelectionRange(pos, pos);
        } catch {
        }
        delete pendingFeedbackCaretRef.current[rowKeyNum];
      };

      requestAnimationFrame(apply);
    },
    []
  );

  const resolveSignedImageUrl = useCallback((file: ReportFileRecord): string => {
    return (
      file.signed_url ??
      file.signedUrl ??
      file.url ??
      file.public_url ??
      file.publicUrl ??
      ''
    );
  }, []);

  const makeImageAttachmentFromFileRecord = useCallback(
    (
      file: ReportFileRecord,
      index: number,
      options?: {
        signedUrl?: string;
        fileKey?: string;
        expiresAtMs?: number;
      }
    ): ImageAttachment => {
      const idBase =
        file.file_id ??
        file.fileId ??
        file.object_key_hash ??
        file.objectKeyHash ??
        file.object_key ??
        file.objectKey ??
        index;
      const id = `file-${String(idBase)}`;
      const name =
        String(
          file.original_name ??
            file.originalName ??
            file.object_key ??
            file.objectKey ??
            `image-${idBase}`
        );
      const url =
        options?.signedUrl ||
        resolveSignedImageUrl(file) ||
        file.object_key ||
        file.objectKey ||
        '';
      const fileKey = (options?.fileKey ?? String(file.object_key ?? file.objectKey ?? '')).trim();
      return {
        id,
        kind: 'url',
        url,
        previewUrl: url,
        name,
        file_id: file.file_id ?? file.fileId ?? null,
        fileKey: fileKey || undefined,
        previewUrlExpiresAtMs: options?.expiresAtMs,
      };
    },
    [resolveSignedImageUrl]
  );

  const extractFileKey = useCallback((file: ReportFileRecord): string => {
    return String(file.object_key ?? file.objectKey ?? '').trim();
  }, []);

  const shouldIncludeFileRecord = useCallback((file: ReportFileRecord): boolean => {
    const status = String(file.status ?? '').trim().toUpperCase();
    if (!status) return true;
    return status === 'Y';
  }, []);

  const buildImageAttachmentsWithSignedUrls = useCallback(
    (
      files: ReportFileRecord[],
      signedUrlMap: Map<string, { url: string; expiresAtMs?: number }>
    ): ImageAttachment[] => {
      return files
        .filter(shouldIncludeFileRecord)
        .map((file, index) => {
          const key = extractFileKey(file);
          const signedUrl = key ? signedUrlMap.get(key) : undefined;
          const attachment = makeImageAttachmentFromFileRecord(file, index, {
            signedUrl: signedUrl?.url,
            fileKey: key,
            expiresAtMs: signedUrl?.expiresAtMs,
          });
          return attachment.previewUrl ? attachment : null;
        })
        .filter(Boolean) as ImageAttachment[];
    },
    [extractFileKey, makeImageAttachmentFromFileRecord, shouldIncludeFileRecord]
  );

  const buildImageAttachmentsFromFiles = useCallback(
    async (files: ReportFileRecord[]): Promise<ImageAttachment[]> => {
      const usable = files.filter(shouldIncludeFileRecord);
      if (usable.length === 0) return [];

      const keys = Array.from(
        new Set(usable.map(extractFileKey).filter((key) => key.length > 0))
      );

      const signedUrlMap = new Map<string, { url: string; expiresAtMs?: number }>();
      if (keys.length > 0) {
        try {
          const downloadResult = await postPresignedDownloadUrlsWithMeta(keys);
          downloadResult.items.forEach((item) => {
            if (item?.key && item?.url) {
              signedUrlMap.set(String(item.key), {
                url: String(item.url),
                expiresAtMs: downloadResult.expiresAtMs,
              });
            }
          });
        } catch (e) {
          console.error('Failed to fetch presigned download URLs', e);
        }
      }

      return buildImageAttachmentsWithSignedUrls(usable, signedUrlMap);
    },
    [
      buildImageAttachmentsWithSignedUrls,
      extractFileKey,
      shouldIncludeFileRecord,
    ]
  );

  const revokePreviewUrls = useCallback((items: ImageAttachment[]) => {
    items.forEach((img) => {
      if (img.kind === 'file' && img.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(img.previewUrl);
      }
    });
  }, []);

  const getImagesForKey = useCallback(
    (key: string) => imageAttachmentsByKey[key] ?? fileAttachmentsByKey[key] ?? [],
    [fileAttachmentsByKey, imageAttachmentsByKey]
  );

  const refreshImageAttachmentUrl = useCallback(
    async (key: string, image: ImageAttachment): Promise<ImageAttachment | null> => {
      const fileKey = String(image.fileKey ?? '').trim();
      if (!fileKey) return null;

      try {
        const downloadResult = await postPresignedDownloadUrlsWithMeta([fileKey]);
        const item = downloadResult.items.find(
          (candidate) => String(candidate?.key ?? '') === fileKey
        );
        if (!item?.url) return null;

        const updatedImage: Extract<ImageAttachment, { kind: 'url' }> = {
          id: image.id,
          kind: 'url',
          url: String(item.url),
          previewUrl: String(item.url),
          name: image.name,
          file_id: image.file_id ?? null,
          fileKey,
          previewUrlExpiresAtMs: downloadResult.expiresAtMs,
        };

        const replaceImage = (current: ImageAttachment[] | undefined) => {
          if (!current) return current;
          let changed = false;
          const next = current.map((itemForKey) => {
            if (itemForKey.id !== image.id) return itemForKey;
            changed = true;
            return updatedImage;
          });
          return changed ? next : current;
        };

        setFileAttachmentsByKey((prev) => {
          const current = prev[key];
          const next = replaceImage(current);
          return next && next !== current ? { ...prev, [key]: next } : prev;
        });
        setImageAttachmentsByKey((prev) => {
          const current = prev[key];
          const next = replaceImage(current);
          return next && next !== current ? { ...prev, [key]: next } : prev;
        });

        return updatedImage;
      } catch (e) {
        console.error('Failed to refresh presigned download URL', e);
        return null;
      }
    },
    []
  );

  const uploadImagesForKey = useCallback(
    async (params: {
      key: string;
      wrId?: number | null;
      nextImages: ImageAttachment[];
      prevImages: ImageAttachment[];
      successMessage?: string;
      cleanupOnFailure?: ImageAttachment[];
      cleanupOnSuccess?: ImageAttachment[];
    }) => {
      const hasWrId = Number.isFinite(params.wrId);
      if (!hasWrId) {
        setImageAttachmentsByKey((prev) => ({ ...prev, [params.key]: params.prevImages }));
        setSnackbarMessage('画像アップロードに失敗しました: wr_id が不明です');
        setSnackbarOpen(true);
        params.cleanupOnFailure?.length && revokePreviewUrls(params.cleanupOnFailure);
        return;
      }

      setSnackbarMessage('画像の反映処理中です');
      setSnackbarOpen(true);
      setImageUploadByKey((prev) => ({ ...prev, [params.key]: true }));
      try {
        const updatedImages = await uploadImageAttachments({
          wrId: Number(params.wrId),
          images: params.nextImages,
          createBy: selfLoginId || undefined,
          updateBy: selfLoginId || undefined,
        });
        setFileAttachmentsByKey((prev) => ({ ...prev, [params.key]: updatedImages }));
        setImageAttachmentsByKey((prev) => ({ ...prev, [params.key]: updatedImages }));
        if (params.successMessage) {
          setSnackbarMessage(params.successMessage);
          setSnackbarOpen(true);
        }
        if (params.cleanupOnSuccess?.length) {
          revokePreviewUrls(params.cleanupOnSuccess);
        }
      } catch (e) {
        console.error('Failed to upload images', e);
        setImageAttachmentsByKey((prev) => ({ ...prev, [params.key]: params.prevImages }));
        setSnackbarMessage('画像アップロードに失敗しました');
        setSnackbarOpen(true);
        if (params.cleanupOnFailure?.length) {
          revokePreviewUrls(params.cleanupOnFailure);
        }
      } finally {
        setImageUploadByKey((prev) => ({ ...prev, [params.key]: false }));
      }
    },
    [revokePreviewUrls, selfLoginId]
  );

  const handleAddImages = useCallback(
    async (candidates: ImageAddCandidate[]) => {
      const dialog = imageDialogKey;
      if (!dialog) return;
      const key = dialog.key;
      const nextItems: ImageAttachment[] = candidates.map((c, index) => {
        if (c.type === 'url') {
          return {
            id: `img-${key}-url-${Date.now()}-${index}`,
            kind: 'url',
            url: c.url,
            previewUrl: c.url,
            name: c.url,
          };
        }
        const previewUrl = URL.createObjectURL(c.file);
        return {
          id: `img-${key}-file-${Date.now()}-${index}`,
          kind: 'file',
          file: c.file,
          previewUrl,
          name: c.file.name || 'image',
        };
      });

      const prevImages = getImagesForKey(key);
      const nextImages = [...prevImages, ...nextItems];
      setImageAttachmentsByKey((prev) => ({ ...prev, [key]: nextImages }));
      setImageDialogKey(null);
      await uploadImagesForKey({
        key,
        wrId: dialog.wrId,
        nextImages,
        prevImages,
        successMessage: '画像をアップロードしました',
        cleanupOnFailure: nextItems,
      });
    },
    [getImagesForKey, imageDialogKey, uploadImagesForKey]
  );

  const handleConfirmRemoveImage = useCallback(async () => {
    if (!imageDeleteTarget) return;
    const { key, id, wrId } = imageDeleteTarget;
    const prevImages = getImagesForKey(key);
    const removed = prevImages.find((img) => img.id === id);
    const nextImages = prevImages.filter((img) => img.id !== id);
    setImageAttachmentsByKey((prev) => ({ ...prev, [key]: nextImages }));
    setImageDeleteTarget(null);
    await uploadImagesForKey({
      key,
      wrId,
      nextImages,
      prevImages,
      successMessage: '画像を削除しました',
      cleanupOnSuccess: removed ? [removed] : undefined,
    });
  }, [getImagesForKey, imageDeleteTarget, uploadImagesForKey]);

  useEffect(() => {
    return () => {
      Object.values(imageAttachmentsByKey).flat().forEach((img) => {
        if (img.kind === 'file' && img.previewUrl.startsWith('blob:')) {
          URL.revokeObjectURL(img.previewUrl);
        }
      });
    };
  }, [imageAttachmentsByKey]);
  const WS_SELF_ID = useMemo(() => {
    const id = selfLoginId || (user as any)?.signInDetails?.loginId || '';
    return String(id).trim();
  }, [selfLoginId, user]);

  // Editable report data and per-field save/undo state.
  const [editedResults, setEditedResults] = useState<Report[]>([]);
  const [titleStatuses, setTitleStatuses] = useState<SaveStatus[]>([]);
  const [bodyStatuses, setBodyStatuses] = useState<SaveStatus[]>([]);
  const [feedbackStatuses, setFeedbackStatuses] = useState<Record<number, SaveStatus>>({});
  const [syncEnabledList, setSyncEnabledList] = useState<boolean[]>([]);
  const [feedbackTexts, setFeedbackTexts] = useState<Record<number, string>>({});
  const [undoTitleMap, setUndoTitleMap] = useState<Record<number, string>>({});
  const [undoBodyMap, setUndoBodyMap] = useState<Record<number, string>>({});
  const [undoFeedbackMap, setUndoFeedbackMap] =
    useState<Record<number, string>>({});

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const copyWrIdUrl = async (wrId?: number | null) => {
    if (!Number.isFinite(Number(wrId))) return;
    const id = Number(wrId);
    const url =
      `${window.location.origin}${window.location.pathname}` +
      `?wr_id=${encodeURIComponent(String(id))}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const el = document.createElement('textarea');
        el.value = url;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setSnackbarMessage('URLをコピーしました');
      setSnackbarOpen(true);
    } catch (e) {
      console.error('URLのコピーに失敗しました:', e);
      setSnackbarMessage('URLのコピーに失敗しました');
      setSnackbarOpen(true);
    }
    scheduleDraftSave(wrId);
  };
  const [versionSaving, setVersionSaving] = useState<Record<number, boolean>>({});
  const autoVersionSaveCounterRef = useRef<Record<number, number>>({});
  const exitFlushAtRef = useRef<number>(0);
  const draftDbPromiseRef = useRef<Promise<IDBDatabase> | null>(null);
  const draftSaveTimersRef = useRef<Record<number, number>>({});
  const draftQueueTimerRef = useRef<number | null>(null);
  const draftQueueInFlightRef = useRef(false);
  const feedbackDirectSaveWrIdsRef = useRef<Set<number>>(new Set());

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyWrId, setHistoryWrId] = useState<number | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetIdx, setConfirmTargetIdx] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmTargetField, setConfirmTargetField] = useState<'title' | 'body' | null>(null);

  const [requiredItems, setRequiredItems] = useState<WeeklyRequiredItemsResponse | null>(null);
  const [wrPublicSettings, setWrPublicSettings] = useState<WeeklyReportPublicSettingRow[] | null>(null);
  const [commonAttrMetaSettings, setCommonAttrMetaSettings] = useState<CommonAttributeMetaSetting[] | null>(null);
  const [communicationCountsByWrId, setCommunicationCountsByWrId] = useState<Record<number, number>>({});
  const [communicationNoticeByWrId, setCommunicationNoticeByWrId] = useState<Record<number, boolean>>({});

  const reportTypeScopeDisplayByOptionId = useMemo(
    () =>
      buildWeeklyReportPublicScopeDisplayMap({
        rows: wrPublicSettings,
        departments: requiredItems?.departments ?? [],
        teams: requiredItems?.teams ?? [],
        maxVisible: 3,
      }),
    [wrPublicSettings, requiredItems?.departments, requiredItems?.teams]
  );

  const getReportTypeScopeDisplay = useCallback(
    (optionId: number | string) => {
      if (wrPublicSettings == null) return LOADING_PUBLIC_SCOPE_DISPLAY;
      return reportTypeScopeDisplayByOptionId[String(optionId)] ?? DEFAULT_PUBLIC_SCOPE_DISPLAY;
    },
    [reportTypeScopeDisplayByOptionId, wrPublicSettings]
  );

  const normalizeMultipleSelect = (value: any): boolean => {
    if (value == null) return false;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    const s = String(value).trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'y';
  };

  const reportTypeMultipleSelectMap = useMemo<Map<string, boolean>>(() => {
    const map = new Map<string, boolean>();
    const list = Array.isArray(commonAttrMetaSettings) ? commonAttrMetaSettings : [];
    list.forEach((row) => {
      const attrId = Number(row?.attr_id);
      if (!Number.isFinite(attrId)) return;
      const depId = Number(row?.department_id ?? 0);
      const teamId = Number(row?.team_id ?? 0);
      const key = `${depId}:${teamId}:${attrId}`;
      map.set(key, normalizeMultipleSelect(row?.multiple_select));
    });
    return map;
  }, [commonAttrMetaSettings]);

  const getReportTypeMultipleSelect = (
    departmentId?: number | null,
    teamId?: number | null,
    attrId?: number | null
  ): boolean => {
    if (attrId == null) return false;
    const depId = Number(departmentId ?? 0);
    const tmId = Number(teamId ?? 0);
    const exactKey = `${depId}:${tmId}:${Number(attrId)}`;
    if (reportTypeMultipleSelectMap.has(exactKey)) {
      return reportTypeMultipleSelectMap.get(exactKey) ?? false;
    }
    const depKey = `${depId}:0:${Number(attrId)}`;
    if (reportTypeMultipleSelectMap.has(depKey)) {
      return reportTypeMultipleSelectMap.get(depKey) ?? false;
    }
    const fallbackKey = `0:0:${Number(attrId)}`;
    if (reportTypeMultipleSelectMap.has(fallbackKey)) {
      return reportTypeMultipleSelectMap.get(fallbackKey) ?? false;
    }
    return false;
  };

  const buildIdLabelMap = (
    items: any[] | null | undefined,
    idKeys: string[],
    labelKeys: string[]
  ): Record<number, string> => {
    const map: Record<number, string> = {};
    const arr = Array.isArray(items) ? items : [];
    for (const it of arr as any[]) {
      let idVal: any = undefined;
      for (const k of idKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null) { idVal = v; break; }
      }
      const id = Number(idVal);
      if (!Number.isFinite(id)) continue;

      let label = '';
      for (const k of labelKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null) {
          const s = String(v);
          if (s) { label = s; break; }
        }
      }
      if (label) map[id] = label;
    }
    return map;
  };

  const buildIdOrderMap = (
    items: any[] | null | undefined,
    idKeys: string[],
    orderKeys: string[]
  ): Record<number, number> => {
    const map: Record<number, number> = {};
    const arr = Array.isArray(items) ? items : [];
    for (const it of arr as any[]) {
      let idVal: any = undefined;
      for (const k of idKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null) { idVal = v; break; }
      }
      const id = Number(idVal);
      if (!Number.isFinite(id)) continue;

      let orderVal: any = undefined;
      for (const k of orderKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null) { orderVal = v; break; }
      }
      const order = Number(orderVal);
      if (!Number.isFinite(order)) continue;
      map[id] = order;
    }
    return map;
  };

  const buildIdNumberMap = (
    items: any[] | null | undefined,
    idKeys: string[],
    valueKeys: string[]
  ): Record<number, number> => {
    const map: Record<number, number> = {};
    const arr = Array.isArray(items) ? items : [];
    for (const it of arr as any[]) {
      let idVal: any = undefined;
      for (const k of idKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null) { idVal = v; break; }
      }
      const id = Number(idVal);
      if (!Number.isFinite(id)) continue;

      let rawVal: any = undefined;
      for (const k of valueKeys) {
        const v = (it as any)?.[k];
        if (typeof v !== 'undefined' && v !== null && v !== '') { rawVal = v; break; }
      }
      if (typeof rawVal === 'undefined') continue;
      const num = Number(rawVal);
      if (!Number.isFinite(num)) continue;
      map[id] = num;
    }
    return map;
  };

  const teamLabelMap = useMemo<Record<number, string>>(
    () => buildIdLabelMap(requiredItems?.teams, ['id', 'team_id', 'value'], ['label', 'value']),
    [requiredItems]
  );

  const teamOrderMap = useMemo<Record<number, number>>(
    () => buildIdOrderMap(requiredItems?.teams, ['id', 'team_id', 'value'], ['order_number']),
    [requiredItems]
  );

  const teamBodyMaxLengthMap = useMemo<Record<number, number>>(
    () => buildIdNumberMap(
      requiredItems?.teams,
      ['id', 'team_id', 'value'],
      ['body_max_length', 'team_body_max_length', 'bodyMaxLength', 'max_length']
    ),
    [requiredItems]
  );

  const requiredTeams: any[] = useMemo(() => {
    if (!requiredItems || !Array.isArray(requiredItems.teams)) return [];
    return requiredItems.teams;
  }, [requiredItems]);

  const teamBodyMaxLengthByValueMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const it of requiredTeams) {
      const raw =
        (it as any)?.body_max_length ??
        (it as any)?.team_body_max_length ??
        (it as any)?.bodyMaxLength ??
        (it as any)?.max_length;
      const num = Number(raw);
      if (!Number.isFinite(num)) continue;
      const label = (it as any)?.label;
      const value = (it as any)?.value;
      if (label) map[String(label)] = num;
      if (value) map[String(value)] = num;
    }
    return map;
  }, [requiredTeams]);

  const enlightSyncUnavailableReason =
    'ご利用のチームではEnlight同期が設定されていません。詳しくは管理者にご確認ください。';

  const teamDocsetStateMap = useMemo<Map<number, boolean>>(() => {
    const map = new Map<number, boolean>();
    if (requiredTeams.length === 0) return map;

    // docset_id がレスポンス定義にまだ無い環境では、従来動作（全て有効）を維持する
    const hasDocsetIdField = requiredTeams.some((t: any) =>
      Object.prototype.hasOwnProperty.call(t ?? {}, 'docset_id')
    );
    if (!hasDocsetIdField) return map;

    requiredTeams.forEach((t: any) => {
      const teamId = Number((t as any)?.team_id ?? (t as any)?.id ?? (t as any)?.value);
      if (!Number.isFinite(teamId)) return;
      const raw = (t as any)?.docset_id;
      const enabled = !(raw === null || raw === undefined || String(raw).trim() === '');
      map.set(teamId, enabled);
    });
    return map;
  }, [requiredTeams]);

  const isEnlightSyncAvailableForTeam = useCallback(
    (teamId?: number | null): boolean => {
      if (!Number.isFinite(Number(teamId))) return true;
      const id = Number(teamId);
      // map が空の場合は docset_id 非対応レスポンスとみなし、従来動作を維持
      if (teamDocsetStateMap.size === 0) return true;
      return teamDocsetStateMap.get(id) ?? true;
    },
    [teamDocsetStateMap]
  );

  const departmentLabelMap = useMemo<Record<number, string>>(
    () => buildIdLabelMap(requiredItems?.departments, ['id', 'department_id', 'value'], ['label', 'value']),
    [requiredItems]
  );

  const departmentOrderMap = useMemo<Record<number, number>>(
    () => buildIdOrderMap(requiredItems?.departments, ['id', 'department_id', 'value'], ['order_number']),
    [requiredItems]
  );

  const getReportTypeAttrId = (
    departmentId?: number | null,
    teamId?: number | null
  ): number | null => {
    const filtered = filterReportTypesForOrg(
      requiredItems?.reportTypes,
      departmentId,
      teamId
    ) as ReportTypeOption[];
    return resolveReportTypeAttrId(filtered);
  };

  const getRowReportTypeOptions = (
    departmentId?: number | null,
    teamId?: number | null
  ): ReportTypeOption[] => {
    return filterReportTypesForOrg(
      requiredItems?.reportTypes,
      departmentId,
      teamId
    ) as ReportTypeOption[];
  };

  const [reportSelections, setReportSelections] = useState<Record<number, string>>({});
  const reportSelectionsRef = useRef<Record<number, string>>({});
  useEffect(() => {
    reportSelectionsRef.current = reportSelections;
  }, [reportSelections]);
  const [reportTypeSavingByWrId, setReportTypeSavingByWrId] = useState<Record<number, boolean>>({});
  const reportTypeSavingByWrIdRef = useRef<Record<number, boolean>>({});
  useEffect(() => {
    reportTypeSavingByWrIdRef.current = reportTypeSavingByWrId;
  }, [reportTypeSavingByWrId]);

  const normalizeDbAttrValue = (v: any): string | null => {
    if (v == null) return null;
    if (typeof v === 'string') {
      const s = v.trim();
      if (s === '') return null;
      const low = s.toLowerCase();
      if (low === 'null' || low === '[null]' || low === '(null)') return null;
      return s;
    }
    return String(v);
};

const normalizeReportTypeToken = normalizeReportTypeSelectionKey;

const parseReportTypeSelection = (
    raw: string | null | undefined,
    r: Report,
    options?: ReportTypeOption[]
  ): { values: string[]; normalized: string } => {
    const source = String(raw ?? '').trim();
    if (source === '') return { values: [], normalized: '' };
    const opts = options ?? getRowReportTypeOptions(r?.department, r?.team);

    const match = (token: string): ReportTypeOption | undefined =>
      opts.find(
        (opt) =>
          normalizeReportTypeToken(opt.value) === token ||
          normalizeReportTypeToken(opt.label) === token
      );

    const knownValues = opts.flatMap((option) => [option.value, option.label]);
    const resolvedTokens = resolveKnownMultiSelectTokens(source, knownValues);
    const mapped = resolvedTokens.map((token) => {
      const key = normalizeReportTypeToken(token);
      const hit = match(key);
      return hit ? normalizeReportTypeToken(hit.value) : token;
    });
    const values = Array.from(new Set(mapped.filter((t) => t !== '')));
    return { values, normalized: values.join(' / ') };
  };

  const toTime = (x: any): number => {
    const dt =
      x?.update_dt ?? x?.UPDATE_DT ??
      x?.create_dt ?? x?.CREATE_DT ?? null;
    if (typeof dt === 'string') {
      const t = Date.parse(dt);
      if (!Number.isNaN(t)) return t;
    }
    if (x?.fb_id != null) {
      const n = Number(x.fb_id);
      if (!Number.isNaN(n)) return n;
    }
    return -Infinity;
  };

  const formatDateTime = (s: any): string => {
    if (!s) return '';
    try {
      const d = new Date(typeof s === 'string' || typeof s === 'number' ? s : String(s));
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleString('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(s ?? '');
    }
  };

  /** WEEKLY_REPORT_FB_ATTRIBUTE を最優先で評価（見つからなければ undefined） */
  const readFromWeeklyFbAttribute = (r: Report, attrId: number | null): string | null | undefined => {
    const anyR: any = r as any;
    const candidates = [
      'WEEKLY_REPORT_FB_ATTRIBUTE',
      'weekly_report_fb_attribute',
      'weeklyReportFbAttribute',
      'WEEKLY_REPORT_FB_ATTRIBUTES',
      'weekly_report_fb_attributes',
      'weeklyReportFbAttributes',
    ];

    let payload: any = undefined;
    for (const k of candidates) {
      if (typeof anyR[k] !== 'undefined') { payload = anyR[k]; break; }
    }
    if (typeof payload === 'undefined') return undefined;

    if (Array.isArray(payload)) {
      let list = payload;
      if (attrId != null) {
        list = list.filter((x: any) => x?.attr_id == null || Number(x?.attr_id) === Number(attrId));
      }
      if (list.length === 0) return undefined;
      list.sort((a: any, b: any) => toTime(b) - toTime(a));
      const latest = list[0];
      const v = normalizeDbAttrValue(latest?.attr_value);
      return v; // null なら未選択確定、string なら選択値
    }

    if (payload && typeof payload === 'object') {
      if (attrId == null || payload?.attr_id == null || Number(payload?.attr_id) === Number(attrId)) {
        const v = normalizeDbAttrValue(payload?.attr_value);
        return v;
      }
      return undefined;
    }

    return undefined;
  };

  /** `feedback_attributes` / `feedback_attrs` 側（無いなら undefined） */
  const readFromFeedbackAttributes = (r: Report, attrId: number | null): string | null | undefined => {
    const anyR: any = r as any;
    const fbAttrsArr = Array.isArray(anyR?.feedback_attributes?.attributes)
      ? anyR.feedback_attributes.attributes
      : (Array.isArray(anyR?.feedback_attrs) ? anyR.feedback_attrs : null);

    if (Array.isArray(fbAttrsArr)) {
      let list = fbAttrsArr;
      if (attrId != null) {
        list = list.filter((x: any) => x?.attr_id == null || Number(x?.attr_id) === Number(attrId));
      }
      if (list.length === 0) return undefined;
      list.sort((a: any, b: any) => toTime(b) - toTime(a));
      const latest = list[0];
      const v = normalizeDbAttrValue(latest?.attr_value);
      return v;
    }

    if (typeof anyR?.feedback_attr_value !== 'undefined') {
      const v = normalizeDbAttrValue(anyR?.feedback_attr_value);
      return v;
    }

    return undefined;
  };

  /** レコード直下 feedback.body を取得（無ければ undefined） */
  const readFeedbackBodyFromFeedbackField = (r: Report): string | null | undefined => {
    const anyR: any = r as any;
    const fb = anyR?.feedback;
    if (!fb || typeof fb !== 'object') return undefined;
    const v = normalizeDbAttrValue(fb?.body ?? fb?.feedback_body ?? fb?.BODY);
    return v; // string | null
  };

  /** attributes 配列から { attr_name, attr_value } の一覧を生成（最新優先で重複排除） */
  type AttrPair = { attr_name: string; attr_value: string };

  const toFiniteNumberOrNull = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const getAttrDepartmentId = (it: any): number | null =>
    toFiniteNumberOrNull(
      it?.department_id ?? it?.departmentId ?? it?.dept_id ?? it?.deptId
    );

  const getAttrTeamId = (it: any): number | null =>
    toFiniteNumberOrNull(it?.team_id ?? it?.teamId);

  const isAttributeInReportScope = (it: any, report: any): boolean => {
    const attrDeptId = getAttrDepartmentId(it);
    const attrTeamId = getAttrTeamId(it);
    const reportDeptId = toFiniteNumberOrNull(report?.department ?? report?.department_id ?? report?.departmentId);
    const reportTeamId = toFiniteNumberOrNull(report?.team ?? report?.team_id ?? report?.teamId);

    if (attrDeptId != null && attrDeptId > 0 && reportDeptId != null && attrDeptId !== reportDeptId) {
      return false;
    }
    if (attrTeamId != null && attrTeamId > 0 && reportTeamId != null && attrTeamId !== reportTeamId) {
      return false;
    }
    return true;
  };

  const getAttributeScopeRank = (it: any, report: any): number => {
    const attrDeptId = getAttrDepartmentId(it);
    const attrTeamId = getAttrTeamId(it);
    const reportDeptId = toFiniteNumberOrNull(report?.department ?? report?.department_id ?? report?.departmentId);
    const reportTeamId = toFiniteNumberOrNull(report?.team ?? report?.team_id ?? report?.teamId);

    if (attrTeamId != null && attrTeamId > 0 && reportTeamId != null && attrTeamId === reportTeamId) return 3;
    if (attrDeptId != null && attrDeptId > 0 && reportDeptId != null && attrDeptId === reportDeptId) return 2;
    return 1;
  };

  const getAttributesList = (r: Report): AttrPair[] => {
    const anyR: any = r as any;
    const arr: any[] = Array.isArray(anyR?.attributes) ? anyR.attributes : [];
    if (!Array.isArray(arr) || arr.length === 0) return [];

    const excludeAttrId = getReportTypeAttrId(anyR?.department, anyR?.team);

    const byKey: Record<string, { item: any; rank: number }> = {};
    for (const it of arr) {
      if (excludeAttrId != null && Number(it?.attr_id) === Number(excludeAttrId)) continue;
      if (!isAttributeInReportScope(it, anyR)) continue;

      const name = String(
        it?.attr_name ?? it?.name ?? it?.label ?? (it?.attr_id != null ? `attr_${it.attr_id}` : '')
      ).trim();
      const v0 = normalizeDbAttrValue(it?.attr_value);
      if (!name || v0 == null) continue;

      const key = `${name.normalize('NFKC').toLowerCase()}__${v0.normalize('NFKC')}`;
      const prev = byKey[key];
      const rank = getAttributeScopeRank(it, anyR);
      if (!prev || rank > prev.rank || (rank === prev.rank && toTime(it) > toTime(prev.item))) {
        byKey[key] = { item: it, rank };
      }
    }

    const bestRankByName = new Map<string, number>();
    Object.values(byKey).forEach(({ item, rank }) => {
      const name = String(
        item?.attr_name ?? item?.name ?? item?.label ?? (item?.attr_id != null ? `attr_${item.attr_id}` : '')
      ).trim();
      const key = name.normalize('NFKC').toLowerCase();
      bestRankByName.set(key, Math.max(bestRankByName.get(key) ?? 0, rank));
    });

    const pairs: AttrPair[] = [];
    Object.values(byKey).forEach(({ item: it, rank }) => {
      if (excludeAttrId != null && Number(it?.attr_id) === Number(excludeAttrId)) return;

      const name = String(
        it?.attr_name ?? it?.name ?? it?.label ?? (it?.attr_id != null ? `attr_${it.attr_id}` : '')
      ).trim();
      const v0 = normalizeDbAttrValue(it?.attr_value);
      if (!name || v0 == null) return;
      const bestRank = bestRankByName.get(name.normalize('NFKC').toLowerCase()) ?? rank;
      if (bestRank > 1 && rank < bestRank) return;
      pairs.push({ attr_name: name, attr_value: v0 });
    });

    pairs.sort(
      (a, b) =>
        a.attr_name.localeCompare(b.attr_name, 'ja') ||
        a.attr_value.localeCompare(b.attr_value, 'ja')
    );
    return pairs;
  };

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const response = await apiClient.get<WeeklyRequiredItemsResponse>(API_REQUIRED_ITEMS);
        if (mounted) setRequiredItems(response.data);
      } catch (e) {
        console.error('/weekly-required-items 取得に失敗:', e);
      }
    })();

    void (async () => {
      try {
        const response = await shareInFlightRequest(
          'get-wr-public-setting:all',
          () => apiClient.get<WeeklyReportPublicSettingRow[]>(
            API_GET_WR_PUBLIC_SETTING
          ),
        );
        if (mounted) {
          setWrPublicSettings(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('/get_wr_public_setting 取得に失敗:', error);
        if (mounted) setWrPublicSettings([]);
      }
    })();

    return () => { mounted = false; };
  }, []);

  const dataSource = useMemo<Report[]>(
    () => (Array.isArray(results) && results.length > 0 ? results : []),
    [results]
  );

  const SORT_SELECTIONS_STORAGE_KEY = 'weekly-report-sort-selections';
  const DEFAULT_SORT_SELECTIONS: [string, string, string] = ['range:asc', 'deptTeam:asc', 'creator:desc'];
  const SORT_SELECTIONS_RESET_VERSION_KEY = 'weekly-report-sort-selections-reset-version';
  const SORT_SELECTIONS_RESET_VERSION = '1';
  const FIXED_SORT_FIELDS: Array<{ key: SortKey; label: string }> = [
    { key: 'updated', label: '更新日' },
    { key: 'range', label: '開始・終了日' },
    { key: 'creator', label: '作成者' },
    { key: 'deptTeam', label: '課・チーム' },
  ];
  const sortFields: Array<{ key: SortKey; label: string }> = useMemo(() => [
    ...FIXED_SORT_FIELDS,
    ...sortableColumns.map((c) => ({ key: `attr_${c.attr_id}` as AttrSortKey, label: c.attr_name })),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [sortableColumns]);
  const getSortKey = (value: string): SortKey | '' => {
    const key = String(value ?? '').split(':')[0];
    if (sortFields.some((f) => f.key === key)) return key as SortKey;
    return '';
  };
  const getSortDirection = (value: string): SortDirection | '' => {
    const dir = String(value ?? '').split(':')[1] as SortDirection;
    return dir === 'asc' || dir === 'desc' ? dir : '';
  };
  const isValidSortSelectionValue = (value: string): boolean =>
    !!getSortKey(value) && !!getSortDirection(value);
  const normalizeSortSelections = (raw: unknown): [string, string, string] => {
    const source = Array.isArray(raw) ? raw : DEFAULT_SORT_SELECTIONS;
    const next: [string, string, string] = [
      typeof source[0] === 'string' ? source[0] : DEFAULT_SORT_SELECTIONS[0],
      typeof source[1] === 'string' ? source[1] : '',
      typeof source[2] === 'string' ? source[2] : '',
    ];

    if (!isValidSortSelectionValue(next[0])) {
      next[0] = DEFAULT_SORT_SELECTIONS[0];
    }
    if (!isValidSortSelectionValue(next[1])) next[1] = '';
    if (!isValidSortSelectionValue(next[2])) next[2] = '';
    if (!next[1]) next[2] = '';

    const usedKeys: SortKey[] = [];
    for (let i = 0; i < next.length; i += 1) {
      const key = getSortKey(next[i]);
      if (!key) continue;
      if (usedKeys.includes(key)) {
        next[i] = '';
        if (i === 1) next[2] = '';
      } else {
        usedKeys.push(key);
      }
    }

    return next;
  };
  const writeSortSelectionsToStorage = (next: [string, string, string]) => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem(SORT_SELECTIONS_STORAGE_KEY, JSON.stringify(next));
      }
    } catch {
    }
  };
  const readSortSelectionsFromStorage = (): [string, string, string] => {
    if (typeof window === 'undefined') return DEFAULT_SORT_SELECTIONS;
    try {
      const storedResetVersion = localStorage.getItem(SORT_SELECTIONS_RESET_VERSION_KEY);
      if (storedResetVersion !== SORT_SELECTIONS_RESET_VERSION) {
        localStorage.removeItem(SORT_SELECTIONS_STORAGE_KEY);
        localStorage.setItem(SORT_SELECTIONS_RESET_VERSION_KEY, SORT_SELECTIONS_RESET_VERSION);
        return DEFAULT_SORT_SELECTIONS;
      }

      const raw = localStorage.getItem(SORT_SELECTIONS_STORAGE_KEY);
      if (!raw) return DEFAULT_SORT_SELECTIONS;
      const parsed = JSON.parse(raw);
      return normalizeSortSelections(parsed);
    } catch {
      return DEFAULT_SORT_SELECTIONS;
    }
  };
  const [sortSelections, setSortSelections] = useState<string[]>(readSortSelectionsFromStorage);

  const dataSig = useMemo(() => {
    const ids = dataSource
      .map((r) => Number((r as any)?.wr_id ?? (r as any)?.wrId ?? (r as any)?.id))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);

    return `len:${dataSource.length}|ids:${ids.join(',')}`;
  }, [dataSource]);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    setFileAttachmentsByKey({});
  }, [dataSig]);

  useEffect(() => {
    if (!searchPerformed) {
      setCommunicationCountsByWrId({});
      setCommunicationNoticeByWrId({});
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const resp = await apiClient.get<CommonAttributeMetaSetting[]>(
          API_COMMON_ATTRIBUTE_META_SETTINGS
        );
        if (!mounted) return;
        const list = Array.isArray(resp.data) ? resp.data : [];
        setCommonAttrMetaSettings(list);
      } catch (e) {
        console.error('/get_common_attribute_meta_settings 取得に失敗:', e);
        if (mounted) setCommonAttrMetaSettings([]);
      }
    })();
    return () => { mounted = false; };
  }, [searchPerformed, dataSig]);

  useEffect(() => {
    if (!searchPerformed) return;
    let mounted = true;
    const wrIds = dataSource
      .map((r) => Number((r as any)?.wr_id ?? (r as any)?.wrId ?? (r as any)?.id))
      .filter((n) => Number.isFinite(n))
      .filter((id, idx, arr) => arr.indexOf(id) === idx);
    if (wrIds.length === 0) {
      setCommunicationCountsByWrId({});
      setCommunicationNoticeByWrId({});
      return;
    }

    (async () => {
      try {
        const resp = await apiClient.post(API_GET_COMMUNICATION_NOTICE, { wr_ids: wrIds });
        if (!mounted) return;
        const raw = (resp as any)?.data;
        const list: CommunicationNotice[] = Array.isArray(raw)
          ? raw
          : (Array.isArray((raw as any)?.data) ? (raw as any).data : (raw ? [raw] : []));
        const next: Record<number, number> = {};
        const noticeNext: Record<number, boolean> = {};
        list.forEach((item) => {
          const id = Number(item?.wr_id ?? item?.wrId ?? item?.id);
          if (!Number.isFinite(id)) return;
          const chat = Number(item?.chat_count ?? item?.chatCount ?? 0);
          const reply = Number(item?.reply_count ?? item?.replyCount ?? 0);
          const total =
            (Number.isFinite(chat) ? chat : 0) +
            (Number.isFinite(reply) ? reply : 0);
          if (total > 0) next[id] = total;
          const hasNotice =
            !!selfLoginId &&
            [...(item?.chats ?? []), ...(item?.replies ?? [])].some((entry) =>
              (entry?.notice_users ?? []).some((u) => String(u).trim() === selfLoginId)
            );
          if (hasNotice) noticeNext[id] = true;
        });
        setCommunicationCountsByWrId(next);
        setCommunicationNoticeByWrId(noticeNext);
      } catch (e) {
        console.error('/get_communication_notice 取得に失敗:', e);
        if (mounted) {
          setCommunicationCountsByWrId({});
          setCommunicationNoticeByWrId({});
        }
      }
    })();
    return () => { mounted = false; };
  }, [searchPerformed, dataSig, dataSource, selfLoginId]);

  const resolveInitialSelection = (r: Report): string => {
    if (requiredItems == null) return '';

    const attrId = getReportTypeAttrId(r?.department, r?.team);

    const fromWeekly = readFromWeeklyFbAttribute(r, attrId);
    if (fromWeekly !== undefined) {
      return parseReportTypeSelection(fromWeekly, r).normalized;
    }

    const anyR: any = r as any;
    const baseAttrs = Array.isArray(anyR?.attributes) ? anyR.attributes : [];
    if (baseAttrs.length > 0) {
      const hit = baseAttrs.find((a: any) => {
        if (attrId == null) return a?.attr_id === 1;
        return Number(a?.attr_id) === Number(attrId);
      });
      if (hit) {
        const v = normalizeDbAttrValue(hit?.attr_value);
        return parseReportTypeSelection(v, r).normalized;
      }
    }

    return '';
  };

  const [originalResults, setOriginalResults] = useState<Report[]>([]);
  const [originalFeedbackTexts, setOriginalFeedbackTexts] = useState<Record<number, string>>({});

  const prevDataSigRef = useRef<string>('__INIT__');

  const didFillSelectionsRef = useRef(false);

  const editedResultsRef = useRef<Report[]>([]);
  useEffect(() => {
    editedResultsRef.current = editedResults;
  }, [editedResults]);
  const originalResultsRef = useRef<Report[]>([]);
  useEffect(() => {
    originalResultsRef.current = originalResults;
  }, [originalResults]);
  const feedbackTextsRef = useRef<Record<number, string>>({});
  useEffect(() => {
    feedbackTextsRef.current = feedbackTexts;
  }, [feedbackTexts]);
  const originalFeedbackTextsRef = useRef<Record<number, string>>({});
  useEffect(() => {
    originalFeedbackTextsRef.current = originalFeedbackTexts;
  }, [originalFeedbackTexts]);
  const feedbackInteractionRevisionRef = useRef<Record<number, number>>({});
  const feedbackSaveChainRef = useRef<Record<number, Promise<void>>>({});
  const syncEnabledListRef = useRef<boolean[]>([]);
  useEffect(() => {
    syncEnabledListRef.current = syncEnabledList;
  }, [syncEnabledList]);
  const updateByRef = useRef<string | undefined>(undefined);
  const updateByUserIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    updateByRef.current = UPDATE_BY;
    updateByUserIdRef.current = UPDATE_BY_USER_ID;
  }, [UPDATE_BY, UPDATE_BY_USER_ID]);
  const authTokenRef = useRef<string | undefined>(undefined);
  const refreshAuthToken = useCallback(async () => {
    try {
      const { tokens } = await fetchAuthSession();
      const idToken = tokens?.idToken?.toString();
      const accessToken = tokens?.accessToken?.toString();
      const mode =
        (process.env.REACT_APP_COGNITO_AUTH_TOKEN_USE as
          | 'id'
          | 'access'
          | 'auto'
          | undefined) ?? 'auto';
      const token =
        mode === 'id' ? idToken : mode === 'access' ? accessToken : (idToken || accessToken);
      if (token) authTokenRef.current = token;
    } catch {}
  }, []);
  useEffect(() => {
    void refreshAuthToken();
  }, [user, refreshAuthToken]);

  const getDraftDb = useCallback((): Promise<IDBDatabase> | null => {
    if (typeof indexedDB === 'undefined') return null;
    if (!draftDbPromiseRef.current) {
      draftDbPromiseRef.current = new Promise((resolve, reject) => {
        const req = indexedDB.open(DRAFT_DB_NAME, 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(DRAFT_STORE_NAME)) {
            db.createObjectStore(DRAFT_STORE_NAME, { keyPath: 'wr_id' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }
    return draftDbPromiseRef.current;
  }, []);

  const putDraftRecord = useCallback(async (record: DraftRecord): Promise<void> => {
    const db = await getDraftDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(DRAFT_STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.objectStore(DRAFT_STORE_NAME).put(record);
    });
  }, [getDraftDb]);

  const deleteDraftRecord = useCallback(async (wrId: number): Promise<void> => {
    const db = await getDraftDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(DRAFT_STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.objectStore(DRAFT_STORE_NAME).delete(wrId);
    });
  }, [getDraftDb]);

  const getDraftRecord = useCallback(async (wrId: number): Promise<DraftRecord | null> => {
    const db = await getDraftDb();
    if (!db) return null;
    return await new Promise<DraftRecord | null>((resolve) => {
      const tx = db.transaction(DRAFT_STORE_NAME, 'readonly');
      const req = tx.objectStore(DRAFT_STORE_NAME).get(wrId);
      req.onsuccess = () => resolve((req.result as DraftRecord) ?? null);
      req.onerror = () => resolve(null);
    });
  }, [getDraftDb]);

  const getAllDraftRecords = useCallback(async (): Promise<DraftRecord[]> => {
    const db = await getDraftDb();
    if (!db) return [];
    return await new Promise<DraftRecord[]>((resolve) => {
      const tx = db.transaction(DRAFT_STORE_NAME, 'readonly');
      const req = tx.objectStore(DRAFT_STORE_NAME).getAll();
      req.onsuccess = () => resolve((req.result as DraftRecord[]) ?? []);
      req.onerror = () => resolve([]);
    });
  }, [getDraftDb]);

  const computeDraftForWrId = useCallback((wrId: number): DraftRecord | null => {
    const list = editedResultsRef.current || [];
    const idx = list.findIndex((r) => Number((r as any)?.wr_id) === wrId);
    if (idx < 0) return null;
    const report = list[idx] as any;
    const orig = (originalResultsRef.current || [])[idx] as any;
    if (!report || !orig) return null;

    const title = String(report?.title ?? '');
    const body = String(report?.body ?? '');
    const origTitle = String(orig?.title ?? '');
    const origBody = String(orig?.body ?? '');
    const feedbackNow = String(
      (feedbackTextsRef.current || {})[wrId] ??
        report?.feedback_body ??
        report?.feedback?.body ??
        ''
    );
    const feedbackOrig = String(
      (originalFeedbackTextsRef.current || {})[wrId] ??
        orig?.feedback_body ??
        orig?.feedback?.body ??
        ''
    );

    const titleDirty = !equalIgnoringEdgeWhitespace(title, origTitle);
    const bodyDirty = !equalIgnoringEdgeWhitespace(body, origBody);
    const feedbackDirty = !equalIgnoringEdgeWhitespace(feedbackNow, feedbackOrig);
    const dirty = titleDirty || bodyDirty || feedbackDirty;

    return {
      wr_id: wrId,
      title,
      body,
      feedback_body: feedbackNow,
      sync_flg: (syncEnabledListRef.current || [])[idx] ? 1 : 0,
      update_by: updateByRef.current,
      update_by_user_id: updateByUserIdRef.current,
      updatedAt: Date.now(),
      dirty,
      titleDirty,
      bodyDirty,
      feedbackDirty,
    };
  }, []);

  const applyServerAckToState = useCallback((draft: DraftRecord) => {
    const wrId = draft.wr_id;
    const list = editedResultsRef.current || [];
    const idx = list.findIndex((r) => Number((r as any)?.wr_id) === wrId);
    if (idx < 0) return;
    const current = list[idx] as any;

    const updates: Partial<Report> = {};
    if (draft.titleDirty) {
      const curTitle = String(current?.title ?? '');
      if (equalIgnoringEdgeWhitespace(curTitle, draft.title)) {
        updates.title = curTitle;
      }
    }
    if (draft.bodyDirty) {
      const curBody = String(current?.body ?? '');
      if (equalIgnoringEdgeWhitespace(curBody, draft.body)) {
        updates.body = curBody;
      }
    }
    if (Object.keys(updates).length > 0) {
      setOriginalResults((prev) => {
        const next = prev.map((r, i) => (i === idx ? ({ ...r, ...updates } as Report) : r));
        originalResultsRef.current = next;
        return next;
      });
    }
    if (draft.feedbackDirty) {
      const curFeedback = String(
        (feedbackTextsRef.current || {})[wrId] ??
          current?.feedback_body ??
          current?.feedback?.body ??
          ''
      );
      if (equalIgnoringEdgeWhitespace(curFeedback, draft.feedback_body)) {
        setOriginalFeedbackTexts((prev) => {
          const next = { ...prev, [wrId]: curFeedback };
          originalFeedbackTextsRef.current = next;
          return next;
        });
      }
    }
  }, []);

  const processDraftQueue = useCallback(async () => {
    if (draftQueueInFlightRef.current) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    draftQueueInFlightRef.current = true;
    try {
      const allDrafts = await getAllDraftRecords();
      const dirtyDrafts = allDrafts.filter((d) => d.dirty);
      for (const draft of dirtyDrafts) {
        if (feedbackDirectSaveWrIdsRef.current.has(draft.wr_id)) continue;
        const payload: any = {
          wr_id: draft.wr_id,
          sync_flg: draft.sync_flg,
          update_by: draft.update_by,
          update_by_user_id: draft.update_by_user_id,
        };
        if (draft.titleDirty) payload.title = draft.title;
        if (draft.bodyDirty) payload.body = draft.body;
        if (draft.feedbackDirty) payload.feedback_body = draft.feedback_body;

        try {
          await apiClient.post(API_POST_PATH, payload);
          const latest = await getDraftRecord(draft.wr_id);
          if (latest && latest.updatedAt === draft.updatedAt) {
            await deleteDraftRecord(draft.wr_id);
          }
          applyServerAckToState(draft);
        } catch {
        }
      }
    } finally {
      draftQueueInFlightRef.current = false;
    }
  }, [applyServerAckToState, deleteDraftRecord, getAllDraftRecords, getDraftRecord]);

  const scheduleDraftQueueProcess = useCallback((delay = 800) => {
    if (draftQueueTimerRef.current != null) return;
    draftQueueTimerRef.current = window.setTimeout(() => {
      draftQueueTimerRef.current = null;
      void processDraftQueue();
    }, delay);
  }, [processDraftQueue]);

  const persistDraftForWrId = useCallback(async (wrId: number) => {
    const draft = computeDraftForWrId(wrId);
    if (!draft) return;
    if (!draft.dirty) {
      await deleteDraftRecord(wrId);
      return;
    }
    await putDraftRecord(draft);
    scheduleDraftQueueProcess();
  }, [computeDraftForWrId, deleteDraftRecord, putDraftRecord, scheduleDraftQueueProcess]);

  const scheduleDraftSave = useCallback((wrId?: number | null) => {
    const id = Number(wrId);
    if (!Number.isFinite(id)) return;
    const timers = draftSaveTimersRef.current;
    if (timers[id]) window.clearTimeout(timers[id]);
    timers[id] = window.setTimeout(() => {
      delete timers[id];
      void persistDraftForWrId(id);
    }, DRAFT_SAVE_DEBOUNCE_MS);
  }, [persistDraftForWrId]);

  const flushPendingDraftSaves = useCallback(() => {
    const timers = draftSaveTimersRef.current;
    Object.keys(timers).forEach((key) => {
      const id = Number(key);
      if (!Number.isFinite(id)) return;
      window.clearTimeout(timers[id]);
      delete timers[id];
      void persistDraftForWrId(id);
    });
  }, [persistDraftForWrId]);

  const hydrateDraftsFromIdb = useCallback(async () => {
    const drafts = await getAllDraftRecords();
    const dirtyDrafts = drafts.filter((d) => d.dirty);
    if (dirtyDrafts.length === 0) return;
    const draftMap = new Map<number, DraftRecord>();
    dirtyDrafts.forEach((d) => draftMap.set(d.wr_id, d));

    setEditedResults((prev) => {
      const next = prev.map((r) => {
        const id = Number((r as any)?.wr_id);
        const draft = draftMap.get(id);
        if (!draft) return r;
        const anyR: any = r;
        return {
          ...r,
          title: draft.titleDirty ? draft.title : r.title,
          body: draft.bodyDirty ? draft.body : r.body,
          feedback_body: draft.feedbackDirty ? draft.feedback_body : anyR.feedback_body,
          feedback: draft.feedbackDirty
            ? { ...(anyR.feedback || {}), body: draft.feedback_body }
            : anyR.feedback,
        } as Report;
      });
      editedResultsRef.current = next;
      return next;
    });

    setFeedbackTexts((prev) => {
      const next = { ...prev };
      draftMap.forEach((draft, id) => {
        if (draft.feedbackDirty) next[id] = draft.feedback_body ?? '';
      });
      feedbackTextsRef.current = next;
      return next;
    });

    scheduleDraftQueueProcess(200);
  }, [getAllDraftRecords, scheduleDraftQueueProcess]);

  useEffect(() => {
    if (dataSig === prevDataSigRef.current) return;
    prevDataSigRef.current = dataSig;

    didFillSelectionsRef.current = false;

    const initialData = dataSource.map((r) => ({ ...r }));
    editedResultsRef.current = initialData;
    originalResultsRef.current = initialData;
    setEditedResults(initialData);
    setOriginalResults(initialData);

    setTitleStatuses(Array(initialData.length).fill('idle'));
    setBodyStatuses(Array(initialData.length).fill('idle'));

    const statusMapInit: Record<number, SaveStatus> = {};
    initialData.forEach((r) => {
      const id = Number((r as any)?.wr_id);
      if (Number.isFinite(id)) statusMapInit[id] = 'idle';
    });
    setFeedbackStatuses(statusMapInit);

    const fbMapInit: Record<number, string> = {};
    const selectionMapInit: Record<number, string> = {};
    initialData.forEach((r) => {
      const id = Number((r as any)?.wr_id);
      if (!Number.isFinite(id)) return;

      const fromField = readFeedbackBodyFromFeedbackField(r);
      if (fromField !== undefined) fbMapInit[id] = fromField ?? '';
      else {
        const fallback = normalizeDbAttrValue((r as any)?.feedback_body);
        fbMapInit[id] = fallback ?? '';
      }

      selectionMapInit[id] = resolveInitialSelection(r);
    });
    feedbackTextsRef.current = fbMapInit;
    originalFeedbackTextsRef.current = fbMapInit;
    setFeedbackTexts(fbMapInit);
    setOriginalFeedbackTexts(fbMapInit);
    setReportSelections(selectionMapInit);
    setReportTypeSavingByWrId({});

    setUndoTitleMap({});
    setUndoBodyMap({});
    setUndoFeedbackMap({});
    setImageDialogKey(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSig]);

  // 同じ週報IDのまま sync_flg だけが更新された場合も、最新状態へ追従する。
  // 保存直後はローカル値を優先し、サーバーが追いついた時点で上書き値を破棄する。
  useEffect(() => {
    const overrides = readEnlightSyncOverrides();
    const latestByWrId = new Map(
      dataSource.map((report) => [Number(report.wr_id), report] as const)
    );
    const displayedReports = editedResultsRef.current;
    const reportsInDisplayOrder =
      displayedReports.length === dataSource.length &&
      displayedReports.every((report) =>
        latestByWrId.has(Number(report.wr_id))
      )
        ? displayedReports.map(
            (report) => latestByWrId.get(Number(report.wr_id)) ?? report
          )
        : dataSource;
    const { enabledList, confirmedOverrideWrIds } =
      resolveWeeklyReportEnlightSyncStates(reportsInDisplayOrder, overrides);

    setSyncEnabledList((prev) =>
      prev.length === enabledList.length &&
      prev.every((enabled, idx) => enabled === enabledList[idx])
        ? prev
        : enabledList
    );
    confirmedOverrideWrIds.forEach((wrId) => deleteEnlightSyncOverride(wrId));
  }, [dataSource]);

  useEffect(() => {
    void hydrateDraftsFromIdb();
  }, [dataSig, hydrateDraftsFromIdb]);

  useEffect(() => {
    const handleOnline = () => {
      scheduleDraftQueueProcess(0);
    };
    window.addEventListener('online', handleOnline);
    const id = window.setInterval(() => {
      scheduleDraftQueueProcess(0);
    }, DRAFT_QUEUE_RETRY_MS);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.clearInterval(id);
    };
  }, [scheduleDraftQueueProcess]);

  useEffect(() => {
    if (requiredItems == null) {
      didFillSelectionsRef.current = false;
      return;
    }
    if (didFillSelectionsRef.current) return;

    const list = editedResultsRef.current || [];
    if (!Array.isArray(list) || list.length === 0) return;

    didFillSelectionsRef.current = true;

    setReportSelections((prev) => {
      const next = { ...prev };
      list.forEach((r) => {
        const id = Number((r as any)?.wr_id);
        if (!Number.isFinite(id)) return;
        const cur = String(next[id] ?? '').trim();
        if (cur) return; // 既にあるなら上書きしない
        next[id] = resolveInitialSelection(r);
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredItems]);

  const handleChange = (idx: number, key: keyof Report, value: string) => {
    const source =
      editedResultsRef.current.length > 0
        ? editedResultsRef.current
        : editedResults;
    const wrId = Number(
      (source[idx] as any)?.wr_id ??
        (editedResults[idx] as any)?.wr_id
    );
    const current = source[idx] ?? editedResults[idx];
    if (!current || String((current as any)?.[key] ?? '') === value) {
      return;
    }
    const next = source.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    editedResultsRef.current = next;
    onEdit?.(next);
    setEditedResults(next);
    scheduleDraftSave(wrId);
  };

  const updateTitleStatus = (idx: number, status: SaveStatus) =>
    setTitleStatuses((prev) => {
      if (prev[idx] === status) return prev;
      return prev.map((s, i) => (i === idx ? status : s));
    });
  const updateBodyStatus = (idx: number, status: SaveStatus) =>
    setBodyStatuses((prev) => {
      if (prev[idx] === status) return prev;
      return prev.map((s, i) => (i === idx ? status : s));
    });
  const updateFeedbackStatus = (wrId: number | undefined, status: SaveStatus) =>
    setFeedbackStatuses((prev) => {
      const idNum = Number(wrId);
      if (!Number.isFinite(idNum)) return prev;
      if (prev[idNum] === status) return prev;
      return { ...prev, [idNum]: status };
    });

  const handleFeedbackFocus = (wrId?: number) => {
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;
    feedbackInteractionRevisionRef.current[idNum] =
      (feedbackInteractionRevisionRef.current[idNum] ?? 0) + 1;
    updateFeedbackStatus(idNum, 'editing');
  };

  const handleFeedbackChange = (
    wrId: number | undefined,
    _idx: number | undefined,
    value: string,
    commitToRenderState = true
  ) => {
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;
    const source = feedbackTextsRef.current || {};
    let next = source;
    if (source[idNum] !== value) {
      next = { ...source, [idNum]: value };
      feedbackTextsRef.current = next;
      feedbackInteractionRevisionRef.current[idNum] =
        (feedbackInteractionRevisionRef.current[idNum] ?? 0) + 1;
      scheduleDraftSave(idNum);
    }
    // 行内の入力中はローカルstateだけで描画し、全行の再描画による入力欠落を避ける。
    // blur時にはrefの最新版をReact stateへ一度だけ反映する。
    if (commitToRenderState) setFeedbackTexts(next);
  };

  const handleFeedbackInputChange = (
    wrId: number | undefined,
    idx: number | undefined,
    value: string
  ) => handleFeedbackChange(wrId, idx, value, false);

  const persistSyncFlag = async (wrId: number, enabled: boolean) => {
    try {
      await apiClient.post(API_POST_PATH, {
        wr_id: wrId,
        sync_flg: enabled ? 1 : 0,
        update_by: UPDATE_BY,
        update_by_user_id: UPDATE_BY_USER_ID,
      });
      notifyWeeklyReportUpdated();
    } catch (e) {
      console.error('sync_flg 更新失敗:', e);
    }
  };

  const toggleSync = (idx: number, next?: boolean): void => {
    const wrId = editedResults[idx]?.wr_id;
    const idNum = Number(wrId);
    const current = !!syncEnabledList[idx];
    const nextVal = typeof next === 'boolean' ? next : !current;

    setSyncEnabledList((prev) => prev.map((v, i) => (i === idx ? nextVal : v)));

    if (Number.isFinite(idNum)) {
      writeEnlightSyncOverride(idNum, nextVal);
      persistSyncFlag(idNum, nextVal);
    }
  };

  const postReportTypeAttribute = async (idx: number, value: string | null): Promise<boolean> => {
    const report = editedResults[idx];
    const wrId = report?.wr_id;

    if (!wrId) return false;

    const attrId = getReportTypeAttrId(report.department, report.team);
    if (!attrId) {
      console.error(
        '週報分類の attr_id が解決できませんでした。department:',
        report.department,
        'team:',
        report.team
      );
      return false;
    }

    const normalizedValue = (value == null || value === '') ? null : value;

    try {
      await apiClient.post(
        API_POST_PATH,
        {
          wr_id: wrId,
          update_by: UPDATE_BY,
          update_by_user_id: UPDATE_BY_USER_ID,
          sync_flg: syncEnabledList[idx] ? 1 : 0,
          base_attributes: {
            update_by: UPDATE_BY,
            attributes: [
              { attr_id: attrId, attr_value: normalizedValue },
            ],
          },
        }
      );
      notifyWeeklyReportUpdated('report-type');

      setEditedResults(prev =>
        prev.map((r, i) => {
          if (i !== idx) return r;
          const anyR: any = { ...r };
          const patch = {
            attr_id: attrId,
            attr_value: normalizedValue,
            update_dt: new Date().toISOString(),
          };
          const wkKeys = [
            'WEEKLY_REPORT_FB_ATTRIBUTE',
            'weekly_report_fb_attribute',
            'weeklyReportFbAttribute',
            'WEEKLY_REPORT_FB_ATTRIBUTES',
            'weekly_report_fb_attributes',
            'weeklyReportFbAttributes',
          ];
          let used: string | null = null;
          for (const k of wkKeys) { if (typeof anyR[k] !== 'undefined') { used = k; break; } }
          if (used) {
            if (Array.isArray(anyR[used])) {
              anyR[used] = [...anyR[used], patch];
            } else if (anyR[used] && typeof anyR[used] === 'object') {
              anyR[used] = [anyR[used], patch];
            } else {
              anyR[used] = [patch];
            }
          } else {
            anyR['WEEKLY_REPORT_FB_ATTRIBUTE'] = [patch];
          }
          return anyR as Report;
        })
      );
      return true;
    } catch (err) {
      console.error('分類POSTに失敗:', err);
      return false;
    }
  };

  const changeReport = async (idx: number, clickedValue: string, isMulti: boolean) => {
    const report = editedResultsRef.current[idx] ?? editedResults[idx];
    const wrId = report?.wr_id;
    if (!wrId) return;

    const wrNum = Number(wrId);
    if (!Number.isFinite(wrNum)) return;
    if (reportTypeSavingByWrIdRef.current[wrNum]) return;

    const now = reportSelectionsRef.current[wrNum] ?? '';
    const current = parseReportTypeSelection(now, report).values;
    const currentNormalized = current.join(' / ');
    const clicked = normalizeReportTypeToken(clickedValue);
    if (!clicked) return;

    const clickedKey = normalizeReportTypeToken(clicked);
    const hasClicked = current.some(
      (value) => normalizeReportTypeToken(value) === clickedKey
    );

    const nextValues = isMulti
      ? (hasClicked
        ? current.filter((value) => normalizeReportTypeToken(value) !== clickedKey)
        : [...current, clicked])
      : (hasClicked ? [] : [clicked]);

    const next = nextValues.join(' / ');
    if (next === currentNormalized) return;

    reportTypeSavingByWrIdRef.current = {
      ...reportTypeSavingByWrIdRef.current,
      [wrNum]: true,
    };
    setReportTypeSavingByWrId((prev) => ({ ...prev, [wrNum]: true }));

    startEdit(wrId);

    // APIレスポンスを待たずに、クリック直後に見た目を更新する。
    reportSelectionsRef.current = {
      ...reportSelectionsRef.current,
      [wrNum]: next,
    };
    setReportSelections((prev) => ({ ...prev, [wrNum]: next }));

    try {
      // 単一選択で選択済みボタンを再クリックした場合、nextValues は空配列になる。
      // その場合は null を渡し、postReportTypeAttribute 内で attr_value: null として送信する。
      await postReportTypeAttribute(idx, nextValues.length === 0 ? null : next);
    } finally {
      stopEdit(wrId);
      reportTypeSavingByWrIdRef.current = {
        ...reportTypeSavingByWrIdRef.current,
        [wrNum]: false,
      };
      setReportTypeSavingByWrId((prev) => {
        const nextState = { ...prev };
        delete nextState[wrNum];
        return nextState;
      });
    }
  };

  const handleBlur = async (idx: number, field: 'title' | 'body', nextValue?: string) => {
    const update = field === 'title' ? updateTitleStatus : updateBodyStatus;
    if (typeof nextValue === 'string') {
      handleChange(idx, field, nextValue);
    }
    const currentResults =
      editedResultsRef.current.length > 0
        ? editedResultsRef.current
        : editedResults;
    const currentOriginalResults =
      originalResultsRef.current.length > 0
        ? originalResultsRef.current
        : originalResults;
    const report = currentResults[idx];
    const originalReport = currentOriginalResults[idx];
    if (!report || !originalReport) {
      update(idx, 'idle');
      return;
    }
    const wrId = report.wr_id;

    const titleNowTrim = (report.title ?? '').trim();
    const bodyNowTrim  = (report.body  ?? '').trim();
    if (wrId && titleNowTrim.length === 0 && bodyNowTrim.length === 0) {
      setConfirmTargetIdx(idx);
      setConfirmTargetField(field);
      setConfirmOpen(true);
      update(idx, 'idle');
      return;
    }

    if (field === 'title') {
      const cur  = report.title ?? '';
      const orig = originalReport.title ?? '';
      if (equalIgnoringEdgeWhitespace(cur, orig)) {
        setEditedResults(prev => {
          const next = prev.map((r, i) => (i === idx ? { ...r, title: orig } : r));
          editedResultsRef.current = next;
          return next;
        });
        update(idx, 'idle');
        return;
      }
    } else {
      const cur  = report.body ?? '';
      const orig = originalReport.body ?? '';
      if (equalIgnoringEdgeWhitespace(cur, orig)) {
        setEditedResults(prev => {
          const next = prev.map((r, i) => (i === idx ? { ...r, body: orig } : r));
          editedResultsRef.current = next;
          return next;
        });
        update(idx, 'idle');
        return;
      }
    }

    update(idx, 'saving');
    try {
      if (wrId) {
        const wrNum = Number(wrId);
        if (originalReport) {
          if (field === 'title') {
            const prevTitle = originalReport.title ?? '';
            setUndoTitleMap((prev) => ({
              ...prev,
              [wrNum]: prevTitle,
            }));
          } else {
            const prevBody = originalReport.body ?? '';
            setUndoBodyMap((prev) => ({
              ...prev,
              [wrNum]: prevBody,
            }));
          }
        }

        await apiClient.post(API_POST_PATH, {
          wr_id: wrId,
          title:   field === 'title' ? report.title : undefined,
          body:    field === 'body'  ? report.body  : undefined,
          feedback_body: feedbackTextsRef.current[Number(wrId)] ?? report.feedback_body ?? undefined,
          sync_flg: syncEnabledList[idx] ? 1 : 0,
          update_by: UPDATE_BY,
          update_by_user_id: UPDATE_BY_USER_ID,
        });
        notifyWeeklyReportUpdated();
        setOriginalResults((prev) => {
          const next = prev.map((r, i) => {
            if (i !== idx) return r;
            if (field === 'title') {
              return { ...r, title: report.title ?? '' } as Report;
            }
            return { ...r, body: report.body ?? '' } as Report;
          });
          originalResultsRef.current = next;
          return next;
        });
        void bumpAutoVersionSave(wrId);
      }
    } catch (error) {
      console.error(`${field} の保存失敗:`, error);
      if (isApiError(error)) console.error('エラー詳細:', (error as any).response?.data);
    } finally {
      setTimeout(() => update(idx, 'idle'), 400);
    }
  };

  const handleFeedbackBlur = async (wrId?: number, nextValue?: string) => {
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;
    if (typeof nextValue === 'string') {
      handleFeedbackChange(idNum, undefined, nextValue);
    }
    const currentFeedbackTexts = feedbackTextsRef.current || {};
    const currentOriginalFeedbackTexts = originalFeedbackTextsRef.current || {};
    const currentResults =
      editedResultsRef.current.length > 0
        ? editedResultsRef.current
        : editedResults;
    const idx = currentResults.findIndex((r) => Number((r as any)?.wr_id) === idNum);
    const curText  = currentFeedbackTexts[idNum] ?? '';
    const origText = currentOriginalFeedbackTexts[idNum] ?? originalFeedbackTexts[idNum] ?? '';
    const saveInteractionRevision = feedbackInteractionRevisionRef.current[idNum] ?? 0;

    if (equalIgnoringEdgeWhitespace(curText, origText)) {
      setFeedbackTexts(prev => {
        const next = { ...prev, [idNum]: origText };
        feedbackTextsRef.current = next;
        return next;
      });
      if (idx >= 0) {
        setEditedResults(prev => {
          const next = prev.map((r, i) => {
            if (i !== idx) return r;
            const anyR: any = r;
            return {
              ...r,
              feedback_body: origText,
              feedback: { ...(anyR.feedback || {}), body: origText },
            };
          });
          editedResultsRef.current = next;
          return next;
        });
      }
      updateFeedbackStatus(idNum, 'idle');
      return;
    }

    updateFeedbackStatus(idNum, 'saving');
    try {
      if (idx >= 0) {
        setUndoFeedbackMap(prev => ({
          ...prev,
          [idNum]: origText,
        }));

        feedbackDirectSaveWrIdsRef.current.add(idNum);
        const previousSave = feedbackSaveChainRef.current[idNum] ?? Promise.resolve();
        const saveRequest = previousSave.catch(() => undefined).then(async () => {
          await apiClient.post(API_POST_PATH, {
            wr_id: idNum,
            feedback_body: curText,
            sync_flg: syncEnabledList[idx] ? 1 : 0,
            update_by: UPDATE_BY,
            update_by_user_id: UPDATE_BY_USER_ID,
          });
        });
        feedbackSaveChainRef.current[idNum] = saveRequest;
        await saveRequest;

        notifyWeeklyReportUpdated();

        setOriginalFeedbackTexts((prev) => {
          const next = { ...prev, [idNum]: curText };
          originalFeedbackTextsRef.current = next;
          return next;
        });
        setEditedResults(prev => {
          const latestFeedback = feedbackTextsRef.current[idNum] ?? curText;
          const next = prev.map((r, i) => {
            if (i !== idx) return r;
            const anyR: any = r;
            return {
              ...r,
              feedback_body: latestFeedback,
              feedback: { ...(anyR.feedback || {}), body: latestFeedback },
            };
          });
          editedResultsRef.current = next;
          return next;
        });
      }
    } catch (error) {
      console.error('フィードバックの保存失敗:', error);
      if (isApiError(error)) console.error('エラー詳細:', (error as any).response?.data);
      try {
        await persistDraftForWrId(idNum);
      } catch (draftError) {
        // IndexedDB が利用できない環境でも、React state 上の入力値は消さない。
        console.error('フィードバックのローカル保存失敗:', draftError);
      }
      setSnackbarMessage('フィードバックを保存できませんでした。入力内容を保持して再送します。');
      setSnackbarOpen(true);
    } finally {
      const currentSave = feedbackSaveChainRef.current[idNum];
      if (currentSave) {
        // 後続の保存が登録されている場合、その処理がキューを解放する。
        const releaseSaveQueue = () => {
          if (feedbackSaveChainRef.current[idNum] !== currentSave) return;
          delete feedbackSaveChainRef.current[idNum];
          feedbackDirectSaveWrIdsRef.current.delete(idNum);
          scheduleDraftQueueProcess(0);
        };
        void currentSave.then(releaseSaveQueue, releaseSaveQueue);
      }
      window.setTimeout(() => {
        if (
          canCompleteFeedbackSave(
            feedbackInteractionRevisionRef.current[idNum] ?? 0,
            saveInteractionRevision
          )
        ) {
          updateFeedbackStatus(idNum, 'idle');
        }
      }, 400);
    }
  };

  const flushTitleBodyBeforeVersionSave = async (wrId?: number) => {
    if (wrId == null) return;
    const idx = editedResults.findIndex((r) => Number((r as any)?.wr_id) === Number(wrId));
    if (idx < 0) return;
    const report = editedResults[idx];
    const original = originalResults[idx];
    if (!report || !original) return;

    const titleChanged =
      !equalIgnoringEdgeWhitespace(report.title ?? '', original.title ?? '');
    const bodyChanged =
      !equalIgnoringEdgeWhitespace(report.body ?? '', original.body ?? '');

    if (titleChanged) {
      await handleBlur(idx, 'title');
    }
    if (bodyChanged) {
      await handleBlur(idx, 'body');
    }
  };

  const rollbackTitle = async (idx: number, wrId?: number | string) => {
    if (wrId == null) return;
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;

    const current = editedResults[idx];
    const original = originalResults[idx];
    const lastSaved = original?.title ?? '';
    const currentTitle = current?.title ?? '';
    const prevSaved = undoTitleMap[idNum];

    if (original && !equalIgnoringEdgeWhitespace(currentTitle, lastSaved)) {
      setEditedResults((prev) =>
        prev.map((r, i) =>
          i === idx ? ({ ...r, title: lastSaved } as Report) : r
        )
      );
      updateTitleStatus(idx, 'idle');
      return;
    }

    if (typeof prevSaved === 'string' && !equalIgnoringEdgeWhitespace(lastSaved, prevSaved)) {
      updateTitleStatus(idx, 'saving');

      const target = prevSaved;

      setEditedResults((prev) =>
        prev.map((r, i) =>
          i === idx ? ({ ...r, title: target } as Report) : r
        )
      );

      try {
        await apiClient.post(API_POST_PATH, {
          wr_id: wrId,
          title: target,
          feedback_body: current.feedback_body ?? undefined,
          sync_flg: syncEnabledList[idx] ? 1 : 0,
          update_by: UPDATE_BY,
          update_by_user_id: UPDATE_BY_USER_ID,
        });

        setOriginalResults((prev) =>
          prev.map((r, i) =>
            i === idx ? ({ ...r, title: target } as Report) : r
          )
        );

        setUndoTitleMap((prev) => {
          const next = { ...prev };
          delete next[idNum];
          return next;
        });
      } catch (error) {
        console.error('タイトルのロールバック保存失敗:', error);
        if (isApiError(error)) console.error('エラー詳細:', (error as any).response?.data);
      } finally {
        setTimeout(() => updateTitleStatus(idx, 'idle'), 400);
      }
    }
  };

  const rollbackBody = async (idx: number, wrId?: number | string) => {
    if (wrId == null) return;
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;

    const current = editedResults[idx];
    const original = originalResults[idx];
    const lastSaved = original?.body ?? '';
    const currentBody = current?.body ?? '';
    const prevSaved = undoBodyMap[idNum];

    if (original && !equalIgnoringEdgeWhitespace(currentBody, lastSaved)) {
      setEditedResults((prev) =>
        prev.map((r, i) =>
          i === idx ? ({ ...r, body: lastSaved } as Report) : r
        )
      );
      updateBodyStatus(idx, 'idle');
      return;
    }

    if (typeof prevSaved === 'string' && !equalIgnoringEdgeWhitespace(lastSaved, prevSaved)) {
      updateBodyStatus(idx, 'saving');

      const target = prevSaved;

      setEditedResults((prev) =>
        prev.map((r, i) =>
          i === idx ? ({ ...r, body: target } as Report) : r
        )
      );

      try {
        await apiClient.post(API_POST_PATH, {
          wr_id: wrId,
          body: target,
          feedback_body: current.feedback_body ?? undefined,
          sync_flg: syncEnabledList[idx] ? 1 : 0,
          update_by: UPDATE_BY,
          update_by_user_id: UPDATE_BY_USER_ID,
        });

        setOriginalResults((prev) =>
          prev.map((r, i) =>
            i === idx ? ({ ...r, body: target } as Report) : r
          )
        );

        setUndoBodyMap((prev) => {
          const next = { ...prev };
          delete next[idNum];
          return next;
        });
      } catch (error) {
        console.error('本文のロールバック保存失敗:', error);
        if (isApiError(error)) console.error('エラー詳細:', (error as any).response?.data);
      } finally {
        setTimeout(() => updateBodyStatus(idx, 'idle'), 400);
      }
    }
  };

  const rollbackFeedback = async (wrId?: number, idx?: number) => {
    if (wrId == null) return;
    const idNum = Number(wrId);
    if (!Number.isFinite(idNum)) return;

    const idxFound =
      typeof idx === 'number'
        ? idx
        : editedResults.findIndex(
            (r) => Number((r as any)?.wr_id) === idNum
          );

    const curText  = feedbackTexts[idNum] ?? '';
    const lastSaved = originalFeedbackTexts[idNum] ?? '';
    const prevSaved = undoFeedbackMap[idNum];

    if (!equalIgnoringEdgeWhitespace(curText, lastSaved)) {
      const target = lastSaved;

      setFeedbackTexts(prev => ({
        ...prev,
        [idNum]: target,
      }));

      setEditedResults(prev =>
        prev.map((r, i) => {
          const match =
            idxFound >= 0
              ? i === idxFound
              : Number((r as any)?.wr_id) === idNum;
          if (!match) return r;
          const anyR: any = r;
          return {
            ...r,
            feedback_body: target,
            feedback: { ...(anyR.feedback || {}), body: target },
          };
        })
      );

      updateFeedbackStatus(idNum, 'idle');
      return;
    }

    if (
      typeof prevSaved === 'string' &&
      !equalIgnoringEdgeWhitespace(lastSaved, prevSaved)
    ) {
      updateFeedbackStatus(idNum, 'saving');
      const target = prevSaved;

      setFeedbackTexts(prev => ({
        ...prev,
        [idNum]: target,
      }));

      setEditedResults(prev =>
        prev.map((r, i) => {
          const match =
            idxFound >= 0
              ? i === idxFound
              : Number((r as any)?.wr_id) === idNum;
          if (!match) return r;
          const anyR: any = r;
          return {
            ...r,
            feedback_body: target,
            feedback: { ...(anyR.feedback || {}), body: target },
          };
        })
      );

      try {
        if (idxFound >= 0) {
          await apiClient.post(API_POST_PATH, {
            wr_id: wrId,
            feedback_body: target,
            sync_flg: syncEnabledList[idxFound] ? 1 : 0,
            update_by: UPDATE_BY,
            update_by_user_id: UPDATE_BY_USER_ID,
          });
        } else {
          await apiClient.post(API_POST_PATH, {
            wr_id: wrId,
            feedback_body: target,
            update_by: UPDATE_BY,
            update_by_user_id: UPDATE_BY_USER_ID,
          });
        }

        setOriginalFeedbackTexts(prev => ({
          ...prev,
          [idNum]: target,
        }));

        setUndoFeedbackMap(prev => {
          const next = { ...prev };
          delete next[idNum];
          return next;
        });
      } catch (error) {
        console.error('フィードバックのロールバック保存失敗:', error);
        if (isApiError(error)) {
          console.error('エラー詳細:', (error as any).response?.data);
        }
      } finally {
        setTimeout(() => updateFeedbackStatus(idNum, 'idle'), 400);
      }
    }
  };

  const removeRowAt = (targetIdx: number) => {
    const wrId = editedResults[targetIdx]?.wr_id;
    const wrNum = Number(wrId);

    if (wrId != null) deleteEnlightSyncOverride(Number(wrId));

    setEditedResults(prev => prev.filter((_, i) => i !== targetIdx));
    setOriginalResults(prev => prev.filter((_, i) => i !== targetIdx));
    setTitleStatuses(prev => prev.filter((_, i) => i !== targetIdx));
    setBodyStatuses(prev => prev.filter((_, i) => i !== targetIdx));
    setSyncEnabledList(prev => prev.filter((_, i) => i !== targetIdx));

    if (wrId != null && Number.isFinite(wrNum)) {
      setReportSelections(prev => {
        const next = { ...prev };
        delete next[wrNum];
        return next;
      });
      setReportTypeSavingByWrId((prev) => {
        const next = { ...prev };
        delete next[wrNum];
        return next;
      });
    }

    if (wrId != null) {
      setFeedbackStatuses(prev => {
        const next = { ...prev };
        delete (next as any)[wrId as any];
        return next;
      });
      setFeedbackTexts(prev => {
        const next = { ...prev };
        delete (next as any)[wrId as any];
        return next;
      });
      setOriginalFeedbackTexts(prev => {
        const next = { ...prev };
        delete (next as any)[wrId as any];
        return next;
      });
      setUndoFeedbackMap(prev => {
        const next = { ...prev };
        delete (next as any)[wrId as any];
        return next;
      });
    }
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    const idx = confirmTargetIdx;
    const field = confirmTargetField;
    setConfirmOpen(false);
    if (idx == null || field == null) {
      setConfirmTargetIdx(null);
      setConfirmTargetField(null);
      return;
    }
    const orig = originalResults[idx];
    const restoreVal = field === 'title' ? (orig?.title ?? '') : (orig?.body ?? '');
    setEditedResults(prev =>
      prev.map((r, i) => (i === idx ? { ...r, [field]: restoreVal } as Report : r))
    );
    if (field === 'title') updateTitleStatus(idx, 'idle');
    else updateBodyStatus(idx, 'idle');
    setConfirmTargetIdx(null);
    setConfirmTargetField(null);
  };

  const handleConfirmDelete = async () => {
    const idx = confirmTargetIdx;
    if (idx == null) return;
    const wrId = editedResults[idx]?.wr_id;
    const syncEnabledBeforeRemand = !!syncEnabledList[idx];
    if (!wrId) {
      setConfirmOpen(false);
      setConfirmTargetIdx(null);
      return;
    }
    setIsDeleting(true);
    try {
      await postWeeklyReportRemand({ wr_id: wrId });
      writeRemandEnlightSyncPreference(
        selfLoginId,
        wrId,
        syncEnabledBeforeRemand
      );
      notifyWeeklyReportUpdated();
      removeRowAt(idx);
    } catch (error) {
      console.error('週報差し戻し失敗:', error);
      if (isApiError(error)) console.error('エラー詳細:', (error as any).response?.data);
    } finally {
      setIsDeleting(false);
      setConfirmOpen(false);
      setConfirmTargetIdx(null);
    }
  };

  const openHistorySidebar = (wrId?: number) => {
    const id = Number(wrId);
    if (!Number.isFinite(id)) return;
    setHistoryWrId(id);
    setHistoryOpen(true);
  };

  const closeHistorySidebar = () => {
    setHistoryOpen(false);
  };

  // WebSocket presence / lock ownership state.
  const [otherEditingByWrId, setOtherEditingByWrId] = useState<Record<number, string[]>>({});
  const myEditingCountRef = useRef<Record<number, number>>({});
  const activeEditingWrIdRef = useRef<number | null>(null);
  const selfReleaseAtByWrIdRef = useRef<Record<number, number>>({});
  const selfWarnShownRef = useRef<Set<number>>(new Set());
  const [selfDialogWrId, setSelfDialogWrId] = useState<number | undefined>(undefined);
  const subscribeWrIdsRef = useRef<number[]>([]);
  const didUnmount = useRef(false);
  const { sendJsonMessage, lastMessage, readyState } = useAppWebSocket();

  const sendUsingSafe = (wrId: number) => {
    if (!wrId) return;
    const uid = WS_SELF_ID || String(userName || '').trim();
    if (!uid) return;
    try {
      sendStatusUsing(sendJsonMessage, wrId, uid, userName);
    } catch {}
  };

  const sendReleaseSafe = (wrId: number) => {
    if (!wrId) return;
    const uid = WS_SELF_ID || String(userName || '').trim();
    if (!uid) return;
    selfReleaseAtByWrIdRef.current[wrId] = Date.now();
    try {
      sendStatusRelease(sendJsonMessage, wrId, uid, userName);
    } catch {}
  };

  const [beforeLastMessage, setBeforeLastMessage] = useState<MessageEvent<any> | null>(null);

  const [lockOwnerByWrId, setLockOwnerByWrId] = useState<
    Record<number, RowLockOwner>
  >({});

  const canSaveVersion = useCallback(
    (id: number): boolean => {
      const owner = lockOwnerByWrId[id];
      const ownerUserId = String(owner?.userId ?? '').trim();
      const ownerName = String(owner?.name ?? '').trim();
      const selfId = String(WS_SELF_ID ?? '').trim();
      const selfName = String(userName ?? '').trim();
      const isOwnerSelf =
        !!owner &&
        (
          (ownerUserId && !!selfId && ownerUserId === selfId) ||
          (!ownerUserId && ownerName && !!selfName && ownerName === selfName)
        );
      const others = otherEditingByWrId[id] ?? [];
      const locked = others.length > 0 || (!!owner && !isOwnerSelf);
      return !locked && !versionSaving[id];
    },
    [lockOwnerByWrId, otherEditingByWrId, WS_SELF_ID, userName, versionSaving]
  );

  const saveVersion = useCallback(
    async (id: number, reason: 'manual' | 'auto' = 'manual'): Promise<boolean> => {
      if (!Number.isFinite(id)) return false;
      if (!canSaveVersion(id)) return false;

      setVersionSaving((prev) => ({ ...prev, [id]: true }));
      try {
        await postNewWeeklyReportVersion({ wr_id: id });
        const message =
          reason === 'auto'
            ? '\u81ea\u52d5\u3067\u30d0\u30fc\u30b8\u30e7\u30f3\u4fdd\u5b58\u3057\u307e\u3057\u305f'
            : '\u30d0\u30fc\u30b8\u30e7\u30f3\u4fdd\u5b58\u304c\u5b8c\u4e86\u3057\u307e\u3057\u305f';
        setSnackbarMessage(message);
        setSnackbarOpen(true);
        return true;
      } catch (error) {
        console.error('\u30d0\u30fc\u30b8\u30e7\u30f3\u4fdd\u5b58\u306b\u5931\u6557:', error);
        const message =
          reason === 'auto'
            ? '\u81ea\u52d5\u30d0\u30fc\u30b8\u30e7\u30f3\u4fdd\u5b58\u306b\u5931\u6557\u3057\u307e\u3057\u305f'
            : '\u30d0\u30fc\u30b8\u30e7\u30f3\u4fdd\u5b58\u306b\u5931\u6557\u3057\u307e\u3057\u305f';
        setSnackbarMessage(message);
        setSnackbarOpen(true);
        return false;
      } finally {
        setVersionSaving((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    },
    [canSaveVersion]
  );

  const bumpAutoVersionSave = useCallback(
    async (wrId?: number) => {
      const id = Number(wrId);
      if (!Number.isFinite(id)) return;
      const counters = autoVersionSaveCounterRef.current;
      const current = counters[id] ?? 0;
      const next = Math.min(current + 1, AUTO_VERSION_SAVE_EVERY_COMBINED);
      counters[id] = next;
      if (next < AUTO_VERSION_SAVE_EVERY_COMBINED) return;

      const saved = await saveVersion(id, 'auto');
      if (saved) {
        counters[id] = 0;
      }
    },
    [saveVersion]
  );

  const refreshRowByWrId = async (rid: number): Promise<void> => {
    if (!Number.isFinite(rid)) return;
    try {
      const findByWrId = (rows: any[]): Report | null => {
        const hit =
          rows.find((x: any) => Number(x?.wr_id ?? x?.wrId ?? x?.id) === rid) ??
          null;
        return hit as Report | null;
      };

      const fetchSingle = async (params: Record<string, any>): Promise<Report | null> => {
        const resp = await apiClient.get<Report[]>('/weekly-report-search', { params });
        const list = Array.isArray(resp.data)
          ? resp.data
          : (resp.data ? [resp.data as any] : []);
        return findByWrId(list);
      };

      const userDep = String((profile as any)?.division ?? profile?.department ?? '').trim();
      let updated: Report | null = null;

      if (userDep) {
        updated = await fetchSingle({ wr_id: rid, USER_DEP: userDep });
      } else {
        updated = await fetchSingle({ wr_id: rid });
      }

      // Some users (e.g. dep scope mismatch) may not receive wr row with USER_DEP.
      // Fallback to unscoped fetch to keep realtime row refresh working.
      if (!updated && userDep) {
        updated = await fetchSingle({ wr_id: rid });
      }

      if (!updated) return;

      const fbFromField = readFeedbackBodyFromFeedbackField(updated as Report);
      const fbFallback = normalizeDbAttrValue((updated as any)?.feedback_body);
      const fbText =
        fbFromField !== undefined ? (fbFromField ?? '') : (fbFallback ?? '');
      const selection = resolveInitialSelection(updated as Report);

      const currentReports = editedResultsRef.current;
      const currentOriginalReports = originalResultsRef.current;
      const idxToUpdate = currentReports.findIndex(
        (r) => Number((r as any)?.wr_id) === rid
      );
      if (idxToUpdate === -1) return;

      const currentReport = currentReports[idxToUpdate];
      const currentOriginal = currentOriginalReports[idxToUpdate];
      const currentFeedback = feedbackTextsRef.current[rid] ?? '';
      const originalFeedback = originalFeedbackTextsRef.current[rid] ?? '';
      const feedbackResolution = resolveFeedbackAfterServerRefresh({
        currentText: currentFeedback,
        originalText: originalFeedback,
        serverText: fbText,
      });
      const preserveTitle = !!currentOriginal && !equalIgnoringEdgeWhitespace(
        currentReport?.title ?? '',
        currentOriginal.title ?? ''
      );
      const preserveBody = !!currentOriginal && !equalIgnoringEdgeWhitespace(
        currentReport?.body ?? '',
        currentOriginal.body ?? ''
      );
      const updatedAny = updated as any;
      const nextEditedReport = {
        ...(updated as Report),
        title: preserveTitle ? currentReport.title : updated.title,
        body: preserveBody ? currentReport.body : updated.body,
        feedback_body: feedbackResolution.currentText,
        feedback: {
          ...(updatedAny.feedback || {}),
          body: feedbackResolution.currentText,
        },
      } as Report;

      const nextEditedResults = [...currentReports];
      nextEditedResults[idxToUpdate] = nextEditedReport;
      editedResultsRef.current = nextEditedResults;
      setEditedResults(nextEditedResults);

      const nextOriginalResults = [...currentOriginalReports];
      nextOriginalResults[idxToUpdate] = { ...(updated as Report) };
      originalResultsRef.current = nextOriginalResults;
      setOriginalResults(nextOriginalResults);

      const nextOriginalFeedbackTexts = {
        ...originalFeedbackTextsRef.current,
        [rid]: feedbackResolution.originalText,
      };
      originalFeedbackTextsRef.current = nextOriginalFeedbackTexts;
      setOriginalFeedbackTexts(nextOriginalFeedbackTexts);

      const nextFeedbackTexts = {
        ...feedbackTextsRef.current,
        [rid]: feedbackResolution.currentText,
      };
      feedbackTextsRef.current = nextFeedbackTexts;
      setFeedbackTexts(nextFeedbackTexts);
      setReportSelections((prev) => ({
        ...prev,
        [rid]: selection,
      }));
    } catch (e) {
      console.error('他者編集後の週報取得に失敗:', e);
    }
  };

  const buildExitSavePayloads = useCallback(() => {
    const edited = editedResultsRef.current || [];
    const original = originalResultsRef.current || [];
    const feedbackMap = feedbackTextsRef.current || {};
    const originalFeedbackMap = originalFeedbackTextsRef.current || {};
    const syncList = syncEnabledListRef.current || [];
    const updateBy = updateByRef.current;
    const updateByUserId = updateByUserIdRef.current;
    const payloads: Record<string, any>[] = [];

    for (let idx = 0; idx < edited.length; idx += 1) {
      const report = edited[idx];
      const orig = original[idx];
      if (!report || !orig) continue;
      const wrId = Number((report as any)?.wr_id ?? (orig as any)?.wr_id);
      if (!Number.isFinite(wrId)) continue;

      const title = String((report as any)?.title ?? '');
      const body = String((report as any)?.body ?? '');
      const origTitle = String((orig as any)?.title ?? '');
      const origBody = String((orig as any)?.body ?? '');

      const feedbackNow = String(
        feedbackMap[wrId] ??
          (report as any)?.feedback_body ??
          (report as any)?.feedback?.body ??
          ''
      );
      const feedbackOrig = String(
        originalFeedbackMap[wrId] ??
          (orig as any)?.feedback_body ??
          (orig as any)?.feedback?.body ??
          ''
      );

      const titleChanged = !equalIgnoringEdgeWhitespace(title, origTitle);
      const bodyChanged = !equalIgnoringEdgeWhitespace(body, origBody);
      const feedbackChanged = !equalIgnoringEdgeWhitespace(feedbackNow, feedbackOrig);

      if (!titleChanged && !bodyChanged && !feedbackChanged) continue;

      const titleNowTrim = title.trim();
      const bodyNowTrim = body.trim();
      const bothEmpty = titleNowTrim.length === 0 && bodyNowTrim.length === 0;
      if (bothEmpty && (titleChanged || bodyChanged)) continue;

      const payload: Record<string, any> = {
        wr_id: wrId,
        sync_flg: syncList[idx] ? 1 : 0,
        update_by: updateBy,
        update_by_user_id: updateByUserId,
      };
      if (titleChanged) payload.title = title;
      if (bodyChanged) payload.body = body;
      if (feedbackChanged) payload.feedback_body = feedbackNow;

      payloads.push(payload);
    }

    return payloads;
  }, []);

  const sendExitSavePayload = useCallback((payload: Record<string, any>): boolean => {
    try {
      const baseUrl = apiClient.defaults.baseURL ?? '';
      const url = baseUrl
        ? `${baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl}${API_POST_PATH}`
        : API_POST_PATH;
      const body = JSON.stringify(payload);
      const token = authTokenRef.current;
      if (token && typeof fetch === 'function') {
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body,
          keepalive: true,
          credentials: 'include',
        });
        return true;
      }
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' });
        const ok = navigator.sendBeacon(url, blob);
        if (ok) return true;
      }
      if (typeof fetch === 'function') {
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
          credentials: 'include',
        });
        return true;
      }
    } catch {}
    return false;
  }, []);

  const flushUnsavedOnExit = useCallback(() => {
    flushPendingDraftSaves();
    const now = Date.now();
    if (now - exitFlushAtRef.current < 800) return;
    const payloads = buildExitSavePayloads();
    if (payloads.length === 0) return;
    exitFlushAtRef.current = now;
    payloads.forEach((payload) => {
      sendExitSavePayload(payload);
    });
  }, [buildExitSavePayloads, flushPendingDraftSaves, sendExitSavePayload]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleBeforeUnload = () => {
      flushUnsavedOnExit();
    };
    const handlePageHide = () => {
      flushUnsavedOnExit();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushUnsavedOnExit();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      flushUnsavedOnExit();
    };
  }, [flushUnsavedOnExit]);

  const handleClickSaveVersion = async (wrId?: number) => {
    const id = Number(wrId);
    if (!Number.isFinite(id)) return;
    await flushTitleBodyBeforeVersionSave(id);
    await saveVersion(id, 'manual');
  };

  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return;
    const counts = myEditingCountRef.current || {};
    Object.keys(counts).forEach((key) => {
      const wrId = Number(key);
      if (!wrId) return;
      if (counts[wrId] > 0) {
        sendUsingSafe(wrId);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [readyState, sendJsonMessage, user, userName]);
  }, [readyState]);

  useEffect(() => {
    if (!lastMessage) return;

    let cancelled = false;

    const unwrapPayload = (input: any): any => {
      if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
      const data = (input as any).data;
      return data !== undefined ? data : input;
    };

    (async () => {
      try {
        const parseWrIds = (ev: MessageEvent<any> | null): number[] | null => {
          if (!ev) return null;
          const raw = ev.data as any;
          let msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
          msg = unwrapPayload(msg);
          if (Array.isArray(msg)) {
            const ids: number[] = [];
            msg.forEach((item: any) => {
              if (!item) return;
              const id = Number(item?.wr_id ?? item?.wrid ?? item?.id);
              if (Number.isFinite(id) && !ids.includes(id)) ids.push(id);
            });
            return ids;
          }
          return null;
        };

        const prevIds = parseWrIds(beforeLastMessage);
        const currIds = parseWrIds(lastMessage);

        if (prevIds && currIds) {
          const removed = prevIds.filter((id) => !currIds.includes(id));
          if (removed.length > 0) {
            for (const rid of removed) {
              if (cancelled) break;
              if (isRecentSelfRelease(selfReleaseAtByWrIdRef.current[rid], Date.now())) {
                delete selfReleaseAtByWrIdRef.current[rid];
                continue;
              }
              await refreshRowByWrId(rid);
            }
          }
        }
      } catch (e) {
        console.error('[WeeklyReport WS] parseWrIds failed', e);
      }

      try {
        const raw = lastMessage.data as any;
        let msg = typeof raw === 'string' ? JSON.parse(raw) : raw;
        msg = unwrapPayload(msg);

        const getId = (v: any): number => {
          return Number(v?.wr_id ?? v?.wrid ?? v?.id);
        };

        const getUserId = (v: any): string => {
          return String(v?.userid ?? v?.user ?? '').trim();
        };

        const getName = (v: any): string => {
          const n = String(v?.username ?? v?.name ?? '').trim();
          if (n) return n;
          const uid = getUserId(v);
          return uid;
        };

        const isSelfIdentity = (uid: string, name: string): boolean => {
          // Prefer userId match. Use name only when userId is missing.
          const selfId = String(WS_SELF_ID ?? '').trim();
          if (uid) return !!selfId && uid === selfId;
          if (name) return !!userName && name === userName;
          return false;
        };

        const isSelfUser = (v: any): boolean => {
          const uid = getUserId(v);
          const name = getName(v);
          return isSelfIdentity(uid, name);
        };

        if (Array.isArray(msg)) {
          const nextOthers: Record<number, string[]> = {};
          const nextOwners: Record<number, { userId: string; name: string }> = {};

          msg.forEach((item) => {
            if (!item) return;
            const wrId = getId(item);
            const userId = getUserId(item);
            const name = getName(item);
            if (!wrId || (!userId && !name)) return;

            const self = isSelfUser(item);

            if (!nextOwners[wrId]) {
              nextOwners[wrId] = { userId, name };
            }

            if (!self && name) {
              if (!nextOthers[wrId]) nextOthers[wrId] = [];
              if (!nextOthers[wrId].includes(name)) {
                nextOthers[wrId].push(name);
              }
            }
          });

          if (!cancelled) {
            setOtherEditingByWrId(nextOthers);
            setLockOwnerByWrId((prev) => {
              const result = { ...prev };

              const presentWrIds = Object.keys(nextOwners).map((k) => Number(k));
              const presentSet = new Set<number>(presentWrIds);

              Object.keys(result).forEach((k) => {
                const id = Number(k);
                if (!presentSet.has(id)) {
                  delete result[id];
                }
              });

              presentWrIds.forEach((id) => {
                const owner = nextOwners[id];
                if (owner) {
                  result[id] = owner;
                }
              });

              return result;
            });
          }
          return;
        }

        if (msg && typeof msg === 'object') {
          if (msg.type === 'presence' && Array.isArray(msg.users) && msg.wr_id != null) {
            const wrId = Number(msg.wr_id);

            const usersRaw = msg.users as any[];
            const ownersForWr: { userId: string; name: string }[] = [];
            const others: string[] = [];

            usersRaw.forEach((u) => {
              let userId = '';
              let name = '';

              if (typeof u === 'string') {
                const s = u.trim();
                if (!s) return;
                userId = s;
                name = s;
              } else if (u && typeof u === 'object') {
                userId = getUserId(u);
                name = getName(u);
              }

              if (!userId && !name) return;

              const isSelf = isSelfIdentity(userId, name);

              ownersForWr.push({ userId, name });

              if (!isSelf && name) {
                others.push(name);
              }
            });

            if (!cancelled) {

              setOtherEditingByWrId((prev) => {
                const next = { ...prev };
                if (others.length > 0) next[wrId] = others;
                else delete next[wrId];
                return next;
              });

              setLockOwnerByWrId((prev) => {
                const next = { ...prev };
                if (ownersForWr.length === 0) {
                  delete next[wrId];
                } else {
                  next[wrId] = ownersForWr[0];
                }
                return next;
              });
            }
            return;
          }

          const wrId = getId(msg);
          const userId = getUserId(msg);
          const name = getName(msg);
          const status = Number(msg.status ?? 0);

          if (wrId && !cancelled) {
            const isSelfMsg = isSelfUser(msg);
            const shouldRefreshOnRelease = status === 9 && !isSelfMsg;
            if (status === 9 && isSelfMsg) {
              delete selfReleaseAtByWrIdRef.current[wrId];
            }

            setOtherEditingByWrId((prev) => {
              const next = { ...prev };
              const arr = next[wrId] ? [...next[wrId]] : [];

              if (name && !isSelfMsg) {
                if (status === 9) {
                  /* WeeklyReport Release */
                  const filtered = arr.filter((n) => n !== name);
                  if (filtered.length > 0) {
                    next[wrId] = filtered;
                  } else {
                    delete next[wrId];
                  }
                } else {
                  if (!arr.includes(name)) arr.push(name);
                  next[wrId] = arr;
                }
              }
              return next;
            });

            setLockOwnerByWrId((prev) => {
              const next = { ...prev };
              const current = next[wrId];

              if (status === 1) {
                if (!current) {
                  next[wrId] = { userId, name };
                }
                return next;
              }

              if (status === 9) {
                if (
                  current &&
                  ((userId && current.userId === userId) ||
                    (name && current.name === name))
                ) {
                  delete next[wrId];
                }
                return next;
              }

              return next;
            });

            if (shouldRefreshOnRelease) {
              void refreshRowByWrId(wrId);
            }
          }
        }
      } catch (e) {
        console.error('[WeeklyReport WS] message parse error', e);
      } finally {
        if (!cancelled) {
          setBeforeLastMessage(lastMessage);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, userName, WS_SELF_ID]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
      const counts = myEditingCountRef.current;
      Object.keys(counts).forEach((key) => {
        const wrId = Number(key);
        if (counts[wrId] > 0) {
          sendReleaseSafe(wrId);
        }
      });
      myEditingCountRef.current = {};
      activeEditingWrIdRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendJsonMessage, user, userName]);

  const releaseOtherEdits = (currentWrId: number) => {
    const counts = { ...myEditingCountRef.current };
    Object.keys(counts).forEach((key) => {
      const wrId = Number(key);
      if (!Number.isFinite(wrId) || wrId === currentWrId) return;
      if (counts[wrId] > 0 && readyState === ReadyState.OPEN) {
        sendReleaseSafe(wrId);
      }
      delete counts[wrId];
    });
    myEditingCountRef.current = counts;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startEdit = (wrId?: number) => {
    if (wrId == null) return;
    const active = activeEditingWrIdRef.current;
    if (active != null && active !== wrId) {
      releaseOtherEdits(wrId);
    }
    activeEditingWrIdRef.current = wrId;
    const counts = { ...myEditingCountRef.current };
    const nextCount = (counts[wrId] || 0) + 1;
    counts[wrId] = nextCount;
    myEditingCountRef.current = counts;
    if (nextCount === 1 && !selfWarnShownRef.current.has(wrId)) {
      selfWarnShownRef.current.add(wrId);
      setSelfDialogWrId(wrId);
    }
    if (nextCount === 1 && readyState === ReadyState.OPEN) {
      sendUsingSafe(wrId);
    }
  };

  const stopEdit = (wrId?: number) => {
    if (wrId == null) return;
    const counts = { ...myEditingCountRef.current };
    const now = Math.max((counts[wrId] || 1) - 1, 0);
    counts[wrId] = now;
    if (now === 0) delete counts[wrId];
    myEditingCountRef.current = counts;
    if (now === 0 && readyState === ReadyState.OPEN) {
      sendReleaseSafe(wrId);
    }
    if (Object.keys(counts).length === 0) {
      activeEditingWrIdRef.current = null;
    }
  };

  // Multi-condition sorting (up to 3 keys).
  const parseSortSelection = (value: string): SortSelection | null => {
    if (!value) return null;
    const [keyRaw, dirRaw] = value.split(':');
    const key = getSortKey(keyRaw);
    const dir = dirRaw === 'desc' ? 'desc' : dirRaw === 'asc' ? 'asc' : null;
    if (!key || !dir) return null;
    return { key, dir };
  };

  const buildDirectionLabel = (key: SortKey, dir: SortDirection) => {
    if (key === 'updated' || key === 'range') {
      return dir === 'asc' ? '古い順' : '新しい順';
    }
    if (key === 'creator') {
      return dir === 'asc' ? '昇順' : '降順';
    }
    return dir === 'asc' ? '正順' : '逆順';
  };

  const formatSortSelectionLabel = (value: string, emptyLabel: string): string => {
    const parsed = parseSortSelection(value);
    if (!parsed) return emptyLabel;
    const field = sortFields.find((f) => f.key === parsed.key);
    if (!field) return emptyLabel;
    return `${field.label}（${buildDirectionLabel(parsed.key, parsed.dir)}）`;
  };

  const buildSortOptions = (excludeKeys: SortKey[]) => {
    const options: Array<{ value: string; label: string }> = [];
    sortFields.forEach((f) => {
      if (excludeKeys.includes(f.key)) return;
      options.push({
        value: `${f.key}:asc`,
        label: `${f.label}（${buildDirectionLabel(f.key, 'asc')}）`,
      });
      options.push({
        value: `${f.key}:desc`,
        label: `${f.label}（${buildDirectionLabel(f.key, 'desc')}）`,
      });
    });
    return options;
  };

  const handleSortChange = (index: number, value: string) => {
    setSortSelections((prev) => {
      const draft: [string, string, string] = [
        typeof prev[0] === 'string' ? prev[0] : DEFAULT_SORT_SELECTIONS[0],
        typeof prev[1] === 'string' ? prev[1] : '',
        typeof prev[2] === 'string' ? prev[2] : '',
      ];
      draft[index] = value;
      const next = normalizeSortSelections(draft);
      writeSortSelectionsToStorage(next);
      return next;
    });
  };

  const sortedEntries = useMemo(() => {
    const base = editedResults.map((r, idx) => ({ r, idx }));
    const active = sortSelections
      .map((v) => parseSortSelection(v))
      .filter((v): v is SortSelection => v != null);
    if (active.length === 0) return base;

    const parseDateOnly = (raw: unknown): number | null => {
      const s = String(raw ?? '').trim();
      if (!s) return null;
      const m = s.match(
        /^(\d{4})-(\d{2})-(\d{2})/
      );
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        const d = Number(m[3]);
        const dt = new Date(y, mo - 1, d, 0, 0, 0);
        const t = dt.getTime();
        return Number.isNaN(t) ? null : t;
      }
      const t = Date.parse(s);
      return Number.isNaN(t) ? null : t;
    };

    const getUpdateTime = (r: Report): number | null => {
      const anyR: any = r as any;
      const raw =
        anyR?.update_dt ?? anyR?.UPDATE_DT ??
        anyR?.updateDate ?? anyR?.UPDATE_DATE ??
        anyR?.update_date ?? anyR?.UPDATE_DATE ??
        null;
      return parseDateOnly(raw);
    };

    const getRangeTime = (r: Report): { start: number | null; end: number | null } => {
      const start = Date.parse(String((r as any)?.start_date ?? ''));
      const end = Date.parse(String((r as any)?.end_date ?? ''));
      return {
        start: Number.isNaN(start) ? null : start,
        end: Number.isNaN(end) ? null : end,
      };
    };

    const compareNumber = (a: number | null, b: number | null, dir: SortDirection) => {
      const aValid = typeof a === 'number';
      const bValid = typeof b === 'number';
      if (!aValid && !bValid) return 0;
      if (!aValid) return 1;
      if (!bValid) return -1;
      const diff = a - b;
      return dir === 'asc' ? diff : -diff;
    };

    const compareString = (a: string | null | undefined, b: string | null | undefined, dir: SortDirection) => {
      const sa = (a ?? '').trim();
      const sb = (b ?? '').trim();
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      const diff = sa.localeCompare(sb, 'ja');
      return dir === 'asc' ? diff : -diff;
    };

    const compareBySelection = (a: Report, b: Report, selection: SortSelection) => {
      switch (selection.key) {
        case 'updated': {
          return compareNumber(getUpdateTime(a), getUpdateTime(b), selection.dir);
        }
        case 'range': {
          const ar = getRangeTime(a);
          const br = getRangeTime(b);
          const startDiff = compareNumber(ar.start, br.start, selection.dir);
          if (startDiff !== 0) return startDiff;
          return compareNumber(ar.end, br.end, selection.dir);
        }
        case 'creator': {
          const aName = (a as any)?.create_by ?? '';
          const bName = (b as any)?.create_by ?? '';
          return compareString(String(aName), String(bName), selection.dir);
        }
        case 'deptTeam': {
          const aDept = departmentLabelMap[Number((a as any)?.department)] ?? (a as any)?.department ?? '';
          const bDept = departmentLabelMap[Number((b as any)?.department)] ?? (b as any)?.department ?? '';
          const aTeam = teamLabelMap[Number((a as any)?.team)] ?? (a as any)?.team ?? '';
          const bTeam = teamLabelMap[Number((b as any)?.team)] ?? (b as any)?.team ?? '';
          const aDeptOrder = departmentOrderMap[Number((a as any)?.department)];
          const bDeptOrder = departmentOrderMap[Number((b as any)?.department)];
          const aTeamOrder = teamOrderMap[Number((a as any)?.team)];
          const bTeamOrder = teamOrderMap[Number((b as any)?.team)];
          const deptOrderDiff = compareNumber(
            Number.isFinite(aDeptOrder) ? aDeptOrder : null,
            Number.isFinite(bDeptOrder) ? bDeptOrder : null,
            selection.dir
          );
          if (deptOrderDiff !== 0) return deptOrderDiff;
          const deptDiff = compareString(String(aDept), String(bDept), selection.dir);
          if (deptDiff !== 0) return deptDiff;
          const teamOrderDiff = compareNumber(
            Number.isFinite(aTeamOrder) ? aTeamOrder : null,
            Number.isFinite(bTeamOrder) ? bTeamOrder : null,
            selection.dir
          );
          if (teamOrderDiff !== 0) return teamOrderDiff;
          return compareString(String(aTeam), String(bTeam), selection.dir);
        }
        default: {
          // カスタムカラム: attr_<id> 形式のソートキー
          const attrMatch = selection.key.match(/^attr_(\d+)$/);
          if (attrMatch) {
            const attrId = Number(attrMatch[1]);
            const col = sortableColumns.find((c) => c.attr_id === attrId);
            if (col) {
              const getOptionOrder = (r: Report): number | null => {
                const attrVals = (r as any)?.attribute_values;
                const list = Array.isArray(attrVals) ? attrVals : [];
                const entry = list.find((v: any) => Number(v?.attr_id) === attrId);
                if (!entry) return null;
                const optionId = Number(entry?.option_id ?? entry?.attr_option_id ?? null);
                if (!Number.isFinite(optionId)) return null;
                const opt = col.options.find((o) => o.option_id === optionId);
                return opt ? opt.order_number : null;
              };
              return compareNumber(getOptionOrder(a), getOptionOrder(b), selection.dir);
            }
          }
          return 0;
        }
      }
    };

    return base
      .slice()
      .sort((a, b) => {
        for (const selection of active) {
          const diff = compareBySelection(a.r, b.r, selection);
          if (diff !== 0) return diff;
        }
        return a.idx - b.idx;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editedResults,
    sortSelections,
    departmentLabelMap,
    teamLabelMap,
    departmentOrderMap,
    teamOrderMap,
    sortableColumns,
  ]);

  const visibleEntries = useMemo(
    () => sortedEntries.slice(0, visibleCount),
    [sortedEntries, visibleCount]
  );

  const mailExportReportsInDisplayOrder = useMemo(() => {
    if (sortedEntries.length === 0) return dataSource;

    const keyOf = (r: Report): string | null => {
      const raw = (r as any)?.wr_id ?? (r as any)?.WR_ID ?? null;
      if (raw == null) return null;
      const key = String(raw).trim();
      return key ? key : null;
    };

    const latestByWrId = new Map<string, Report>();
    for (const r of dataSource) {
      const key = keyOf(r);
      if (!key || latestByWrId.has(key)) return dataSource;
      latestByWrId.set(key, r);
    }

    if (latestByWrId.size !== dataSource.length || sortedEntries.length !== dataSource.length) {
      return dataSource;
    }

    const ordered: Report[] = [];
    for (const { r } of sortedEntries) {
      const key = keyOf(r);
      if (!key) return dataSource;
      const latest = latestByWrId.get(key);
      if (!latest) return dataSource;
      ordered.push(latest);
    }
    return ordered;
  }, [dataSource, sortedEntries]);

  const lastMailExportReportsRef = useRef<Report[]>([]);
  useEffect(() => {
    if (!onMailExportReportsChange) return;
    if (
      areSameReportListByReference(
        lastMailExportReportsRef.current,
        mailExportReportsInDisplayOrder
      )
    ) {
      return;
    }
    lastMailExportReportsRef.current = mailExportReportsInDisplayOrder;
    onMailExportReportsChange(mailExportReportsInDisplayOrder);
  }, [mailExportReportsInDisplayOrder, onMailExportReportsChange]);

  const handleLoadMore = useCallback(() => {
    if (loadMoreLockRef.current || isLoadingMore) return;
    loadMoreLockRef.current = true;
    setIsLoadingMore(true);
    window.setTimeout(() => {
      setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedEntries.length));
    }, 0);
  }, [isLoadingMore, sortedEntries.length]);

  useEffect(() => {
    if (!isLoadingMore) return;
    const id = window.setTimeout(() => setIsLoadingMore(false), 0);
    return () => window.clearTimeout(id);
  }, [isLoadingMore, visibleCount, sortedEntries.length]);

  useEffect(() => {
    if (isLoadingMore) return;
    loadMoreLockRef.current = false;
  }, [isLoadingMore]);

  useEffect(() => {
    fileAttachmentsRequestIdRef.current += 1;
    const requestId = fileAttachmentsRequestIdRef.current;

    const targets = visibleEntries
      .map(({ r, idx }) => {
        const { imageKey } = buildRowIdentity((r as any)?.wr_id, idx);
        const files = Array.isArray((r as any)?.files)
          ? ((r as any).files as ReportFileRecord[])
          : [];
        return { key: imageKey, files };
      })
      .filter((item) => item.files.length > 0)
      .filter((item) => !fileAttachmentsByKey[item.key]);

    if (targets.length === 0) return;

    Promise.all(
      targets.map(async (item) => {
        const attachments = await buildImageAttachmentsFromFiles(item.files);
        return [item.key, attachments] as const;
      })
    )
      .then((entries) => {
        if (requestId !== fileAttachmentsRequestIdRef.current) return;
        const next: Record<string, ImageAttachment[]> = {};
        entries.forEach(([key, attachments]) => {
          if (attachments.length > 0) next[key] = attachments;
        });
        if (Object.keys(next).length === 0) return;
        setFileAttachmentsByKey((prev) => ({ ...prev, ...next }));
      })
      .catch((e) => {
        console.error('Failed to build file attachments', e);
      });
  }, [buildImageAttachmentsFromFiles, fileAttachmentsByKey, visibleEntries]);

  // Context object passed to each row renderer.
  const weeklyReportResultRowContext: WeeklyReportResultRowContext = {
    otherEditingByWrId,
    lockOwnerByWrId,
    selfLoginId: WS_SELF_ID,
    userName,
    titleStatuses,
    bodyStatuses,
    feedbackStatuses,
    getRowReportTypeOptions,
    getReportTypeScopeDisplay,
    getReportTypeAttrId,
    getReportTypeMultipleSelect,
    reportSelections,
    reportTypeSavingByWrId,
    parseReportTypeSelection,
    normalizeReportTypeToken,
    teamLabelMap,
    departmentLabelMap,
    teamBodyMaxLengthMap,
    teamBodyMaxLengthByValueMap,
    formatDateTime,
    getAttributesList,
    originalResults,
    equalIgnoringEdgeWhitespace,
    undoTitleMap,
    undoBodyMap,
    feedbackTexts,
    originalFeedbackTexts,
    undoFeedbackMap,
    communicationCountsByWrId,
    communicationNoticeByWrId,
    imageUploadByKey,
    versionSaving,
    imageAttachmentsByKey,
    fileAttachmentsByKey,
    refreshImageAttachmentUrl,
    copyWrIdUrl,
    handleClickSaveVersion,
    openHistorySidebar,
    setConfirmTargetIdx,
    setConfirmTargetField,
    setConfirmOpen,
    StatusIndicator,
    RowSavingBadge,
    SelfEditingBadge,
    FieldEndActions,
    TranslatableDisplayField,
    LinkifyDisplay,
    TranslatableDisplayTextarea,
    updateTitleStatus,
    pendingTitleCaretRef,
    setTitleInputRef,
    startEdit,
    handleBlur,
    stopEdit,
    LOCKED_BG: isDark ? '#111a2b' : LOCKED_BG,
    SAVING_BG,
    SAVING_SHADOW,
    TITLE_ACTIVE_TEXT_SX,
    LOCKED_FG,
    BODY_TEXT_COLOR: isDark ? '#e2e8f0' : BODY_TEXT_COLOR,
    FIELD_ACTION_PADDING_RIGHT_PX,
    updateBodyStatus,
    setBodyInputRef,
    autoResizeTextarea,
    scheduleScrollCaretIntoView,
    bodyInputRefs,
    getTextareaFromEvent,
    handleResizeStart,
    handleResizeEnd,
    getCaretIndexFromPoint,
    pendingBodyCaretRef,
    setImageDialogKey,
    setImageDeleteTarget,
    RIGHT_COL_SX,
    BTN_ROW_SX,
    requiredItems,
    changeReport,
    FEEDBACK_LABEL_SX,
    TEXTAREA_WRAP_SX,
    rollbackFeedback,
    updateFeedbackStatus,
    pendingFeedbackCaretRef,
    setFeedbackInputRef,
    handleFeedbackFocus,
    handleFeedbackBlur,
    handleFeedbackInputChange,
    toggleSync,
    syncEnabledList,
    isEnlightSyncAvailableForTeam,
    enlightSyncUnavailableReason,
    rollbackTitle,
    rollbackBody,
    imageDialogKey,
    handleAddImages,
    HEADER_ACTION_GROUP_SX,
    HEADER_ACTION_BUTTON_SX,
    HEADER_ICON_STROKE_SX,
    HEADER_CAPTION_SX,
    HEADER_ICON_STROKE_RED_SX,
    HEADER_CAPTION_RED_SX,
    ARTICLES_HEADER_SX,
    isBrowserTranslated,
    BODY_MIN_ROWS,
    WeeklyReportRowCard,
  };

  const usingMock = useMemo(
    () => !(Array.isArray(results) && results.length > 0),
    [results]
  );

  return (
    <>
      {searchPerformed && results?.length === 0 && !usingMock && (
        <div className="no-results">
          <div className="no-results-content">
            <InfoOutlinedIcon sx={{ fontSize: 32, color: '#4a90e2' }} />
            <h3>検索結果がありませんでした</h3>
          </div>
        </div>
      )}

      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Box
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
            p: 2,
            backgroundColor: theme.palette.mode === 'dark' ? '#0b1120' : '#fff',
            maxWidth: 1920,
            m: '0 auto',
          })}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2">該当件数：{dataSource.length} 件</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                並べ替え：
              </Typography>
              <TextField
                select
                size="small"
                label="条件1"
                value={sortSelections[0]}
                onChange={(e) => handleSortChange(0, e.target.value)}
                SelectProps={{
                  renderValue: (selected) => formatSortSelectionLabel(String(selected ?? ''), '条件1'),
                }}
                sx={{ ...SORT_SELECT_BASE_SX }}
              >
                {buildSortOptions([]).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="条件2"
                value={sortSelections[1]}
                onChange={(e) => handleSortChange(1, e.target.value)}
                disabled={!getSortKey(sortSelections[0])}
                SelectProps={{
                  renderValue: (selected) => formatSortSelectionLabel(String(selected ?? ''), ''),
                }}
                sx={{
                  ...SORT_SELECT_BASE_SX,
                  '& .MuiInputBase-root.Mui-disabled': { backgroundColor: isDark ? '#162235' : '#f0f0f0' },
                  '& .MuiInputBase-input.Mui-disabled': { color: 'text.disabled' },
                }}
              >
                <MenuItem value="">未選択</MenuItem>
                {buildSortOptions([getSortKey(sortSelections[0])].filter(Boolean) as SortKey[]).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="条件3"
                value={sortSelections[2]}
                onChange={(e) => handleSortChange(2, e.target.value)}
                disabled={!getSortKey(sortSelections[1])}
                SelectProps={{
                  renderValue: (selected) => formatSortSelectionLabel(String(selected ?? ''), ''),
                }}
                sx={{
                  ...SORT_SELECT_BASE_SX,
                  '& .MuiInputBase-root.Mui-disabled': { backgroundColor: isDark ? '#162235' : '#f0f0f0' },
                  '& .MuiInputBase-input.Mui-disabled': { color: 'text.disabled' },
                }}
              >
                <MenuItem value="">未選択</MenuItem>
                {buildSortOptions(
                  [getSortKey(sortSelections[0]), getSortKey(sortSelections[1])]
                    .filter(Boolean) as SortKey[]
                ).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {visibleEntries.map(({ r, idx }) => {
            const { rowKey } = buildRowIdentity((r as any)?.wr_id, idx);
            return (
              <WeeklyReportResultRow
                key={rowKey}
                r={r}
                idx={idx}
                ctx={weeklyReportResultRowContext}
              />
            );
          })}

        {(sortedEntries.length > visibleCount || sortedEntries.length > 0) && (
          <Box sx={{ position: 'relative', py: 2, minHeight: 40 }}>
            {sortedEntries.length > visibleCount && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  aria-busy={isLoadingMore}
                  sx={{ minWidth: 120 }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      '& .MuiCircularProgress-root': {
                        color: 'primary.main',
                        opacity: 1,
                      },
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      },
                    }}
                  >
                    {isLoadingMore && <CircularProgress size={22} thickness={4} />}
                    <span>{isLoadingMore ? '読み込み中...' : 'さらに表示'}</span>
                  </Box>
                </Button>
              </Box>
            )}
            <Box sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}>
              <Tooltip title="TOPに戻る" arrow>
                <IconButton
                  aria-label="TOPに戻る"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <VerticalAlignTopIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}
        </Box>

        <VersionHistorySidebar
          open={historyOpen}
          wrId={historyWrId ?? undefined}
          onClose={closeHistorySidebar}
        />

      </Box>

      <ConfirmDialog
        open={!!imageDeleteTarget}
        title="画像削除確認"
        description="画像を削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="error"
        confirmVariant="contained"
        processing={imageDeleteTarget ? !!imageUploadByKey[imageDeleteTarget.key] : false}
        onConfirm={handleConfirmRemoveImage}
        onCancel={() => setImageDeleteTarget(null)}
        onClose={() => setImageDeleteTarget(null)}
        disableRestoreFocus
      />

      <ConfirmDialog
        open={confirmOpen}
        title="差し戻し確認"
        description="該当の週報を下書きに差し戻します。よろしいですか？この操作は取り消せません。"
        confirmText="差し戻し"
        confirmColor="error"
        confirmVariant="contained"
        processing={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        onClose={handleCancelDelete}
      />

      <AppSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={() => setSnackbarOpen(false)}
      />

    </>
  );
};

export default Content;
